// ShortAccommodationDetails.tsx
// Luxury Areluxe details page — Black × Gold × Red — Gucci editorial aesthetic
// ALL logic preserved, zero imports removed, all sub-components defined inline

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { RootState, AppDispatch } from "../../../redux/store";
import {
  listenToShortstayData,
} from "../../../redux/shortstaysSlice";
import {
  Star, Heart, Share2, Grid, Bath, BedDouble, Car, CarFront, Toilet, Ruler, Users,
  CarIcon, ShieldCheck, ShieldOff, RulerIcon, MapPin, Award, Crown,
  X, ChevronLeft, ChevronRight, Clock, Calendar, Home, Check,
  ArrowLeft, Wifi, Coffee, Dumbbell, Waves, Dog, Baby, Wind,
  Zap, AlertCircle, MessageCircle,
} from "lucide-react";
import { Bed } from "phosphor-react";
import dayjs from "dayjs";
import { listenToListingAvailability, listenToListingAvailabilityByHours, listenToListingReservationRequests, listenToListingReservations, upsertShortstayRequest } from "../../../redux/shortstayReservationSlice";
import { listenToUserPayouts } from "../../../redux/payoutsSlice";
import { fetchShortRatingsByListing } from "../../../redux/shortRatingSlice";
import { GoogleMap, Marker } from "@react-google-maps/api";
import {
  amenitiesCategories, safetyCategories, rulesCategories,
  EntertainmentAmenities, ChildrenAmenities, KitchenAmenities,
  LaundryAmenities, BedroomAmenities, BathroomAmenities, OfficeAmenities,
  CategoryItem,
} from "../../../CATEGORIES/Categories";
import { useCurrency } from "@cosmoola/react-cosmoola-js";
import { makeThreadId } from "../../../redux/iMessageSlice";
//import logo from "../../../../assets/app/logo.png";
import hostIcon from "../../../assets/icon/host.png";
import bathtubIcon from "../../../assets/icon/bathtub.png";
import bedIcon from "../../../assets/icon/bed.png";
import bedroomIcon from "../../../assets/icon/bedroom.png";
import guestsIcon from "../../../assets/icon/guests.png";
import parkingIcon from "../../../assets/icon/parking.png";
import squarefeetIcon from "../../../assets/icon/squarefeet.png";
import toiletIcon from "../../../assets/icon/toilet.png";
/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS — Black × Gold × Red luxury palette
═══════════════════════════════════════════════════════════ */
const T = {
  gold:         "#C9A84C",
  goldLight:    "#E8C96A",
  goldDark:     "#9A7A2E",
  goldGrad:     "linear-gradient(135deg,#E8C96A 0%,#C9A84C 40%,#9A7A2E 100%)",
  goldSubtle:   "rgba(201,168,76,0.10)",
  goldBorder:   "rgba(201,168,76,0.35)",
  black:        "#0A0A0A",
  carbon:       "#111111",
  onyx:         "#1A1A1A",
  graphite:     "#252525",
  pewter:       "#3A3A3A",
  smoke:        "#6B6B6B",
  silver:       "#A8A8A8",
  pearl:        "#E8E8E8",
  white:        "#FFFFFF",
  red:          "#CC0000",
  redLight:     "#E50914",
  cream:        "#FAFAF8",
  offwhite:     "#F5F5F3",
  green:        "#2E7D32",
  greenBg:      "#E8F5E9",
  serif:        "'Cormorant Garamond', 'Georgia', serif",
  sans:         "'Montserrat', 'Helvetica Neue', sans-serif",
};

const GOLD_GRAD = T.goldGrad;
const MAX_WIDTH = 1340;
const DESKTOP_MIN = 900;

/* ═══════════════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════════════ */
const formatHostingDuration = (dateStr?: string): string => {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  const months = Math.floor(days / 30);
  const years = Math.floor(months / 12);
  if (years >= 1) return `${years} yr${years > 1 ? "s" : ""}`;
  if (months >= 1) return `${months} mo${months > 1 ? "s" : ""}`;
  return `${days} day${days > 1 ? "s" : ""}`;
};

const formatRelativeDate = (dateStr?: string): string => {
  if (!dateStr) return "";
  const now = dayjs();
  const d = dayjs(dateStr);
  const years = now.diff(d, "year");
  if (years >= 1) return `${years}y ago`;
  const months = now.diff(d, "month");
  if (months >= 1) return `${months}mo ago`;
  const weeks = now.diff(d, "week");
  if (weeks >= 1) return `${weeks}w ago`;
  const days = now.diff(d, "day");
  if (days >= 1) return `${days}d ago`;
  return "Today";
};

const renderStars = (rating: number) => {
  return (
    <div style={{ display: "flex", gap: 2, marginTop: 3 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={12}
          fill={i < rating ? T.gold : "transparent"}
          color={i < rating ? T.gold : T.silver}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   HOST LISTINGS INLINE (replaces HostShortHomes import)
═══════════════════════════════════════════════════════════ */

const HostShortHomes: React.FC<{ hostId: string }> = ({ hostId }) => {
  const navigate = useNavigate();
  const listings = useSelector((s: RootState) => s.shortstays.listings);
  const { convertRaw, symbol } = useCurrency();
  const [photoIndexes, setPhotoIndexes] = useState<Record<string, number>>({});
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const hostListings = listings.filter((l: any) => l.hostId === hostId).slice(0, 4);

  const getRateLabel = (rateType?: string) => {
    switch (rateType) {
      case "perNightPerPerson": return "person / night";
      case "perHourPerPerson":  return "person / hr";
      case "perHour":           return "/ hr";
      case "perWeek":           return "/ week";
      case "perMonth":          return "/ month";
      case "perYear":           return "/ year";
      default:                  return "/ night";
    }
  };

  const handleNav = (id: string, dir: 1 | -1, total: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setPhotoIndexes(prev => ({ ...prev, [id]: ((prev[id] || 0) + dir + total) % total }));
  };

  if (!hostListings.length) return (
    <p style={{ color: T.smoke, fontSize: 14 }}>No other listings from this host.</p>
  );

  return (
    <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
      {hostListings.map((l: any) => {
        const photos = l.photos || [];
        const idx = photoIndexes[l.listingId] || 0;
        const photo = photos[idx % Math.max(photos.length, 1)];
        const isHovered = hoveredId === l.listingId;

        return (
          <div
            key={l.listingId}
            onClick={() => navigate(`/roomdetails/${l.listingId}`)}
            onMouseEnter={() => setHoveredId(l.listingId)}
            onMouseLeave={() => setHoveredId(null)}
            style={{ cursor: "pointer" }}
          >
            {/* SQUARE PHOTO */}
            <div style={{ position: "relative", width: "100%", paddingBottom: "100%", borderRadius: 16, overflow: "hidden", background: "#e0e0e0" }}>
              <img
                src={photo || "https://via.placeholder.com/300x300?text=No+Image"}
                alt=""
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.4s ease", transform: isHovered ? "scale(1.04)" : "scale(1)" }}
              />

              {/* DOTS */}
              {photos.length > 1 && (
                <div style={{ position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 4, zIndex: 3 }}>
                  {photos.slice(0, 5).map((_: any, i: number) => (
                    <div key={i} style={{ width: i === idx ? 16 : 6, height: 6, borderRadius: 999, background: i === idx ? "#fff" : "rgba(255,255,255,0.55)", transition: "width 0.25s ease", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }}/>
                  ))}
                </div>
              )}

              {/* HEART */}
              <button
                onClick={e => { e.stopPropagation(); setLiked(p => ({ ...p, [l.listingId]: !p[l.listingId] })); }}
                style={{ position: "absolute", top: 10, right: 10, background: "transparent", border: "none", cursor: "pointer", padding: 4, zIndex: 3, filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.4))" }}
              >
                <Heart size={20} fill={liked[l.listingId] ? T.red : "rgba(0,0,0,0.3)"} color={liked[l.listingId] ? T.red : "#fff"} strokeWidth={1.5}/>
              </button>

              {/* PREV */}
              {photos.length > 1 && isHovered && idx > 0 && (
                <button onClick={e => handleNav(l.listingId, -1, photos.length, e)} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.92)", border: "none", borderRadius: "50%", width: 28, height: 28, display: "grid", placeItems: "center", cursor: "pointer", zIndex: 3, boxShadow: "0 2px 6px rgba(0,0,0,0.2)" }}>
                  <ChevronLeft size={15} color="#111"/>
                </button>
              )}

              {/* NEXT */}
              {photos.length > 1 && isHovered && idx < photos.length - 1 && (
                <button onClick={e => handleNav(l.listingId, 1, photos.length, e)} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.92)", border: "none", borderRadius: "50%", width: 28, height: 28, display: "grid", placeItems: "center", cursor: "pointer", zIndex: 3, boxShadow: "0 2px 6px rgba(0,0,0,0.2)" }}>
                  <ChevronRight size={15} color="#111"/>
                </button>
              )}
            </div>

            {/* BELOW CARD INFO */}
            <div style={{ padding: "10px 4px 0" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.black, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {l.address?.city}{l.address?.country ? `, ${l.address.country}` : ""}
              </div>
              <div style={{ fontSize: 13, color: "#444", marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                <span style={{ fontWeight: 700, color: T.black }}>{l.rate ? `${symbol}${Number(convertRaw(l.rate, "EUR")).toLocaleString()}` : "—"}</span>
                {l.rate && <span style={{ fontWeight: 400, color: T.smoke }}>{" "}{getRateLabel(l.rateType)}</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};










/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
export default function ShortAccommodationDetails() {
  const { listingId } = useParams<{ listingId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const listing       = useSelector((s: RootState) => s.shortstays.currentListing);
  const authUser      = useSelector((s: RootState) => s.auth.user);
  const shortRatings  = useSelector((s: RootState) => s.shortRatings.ratings[listingId || ""] || []);
  const allUsers      = useSelector((s: RootState) => s.auth.allUsers);
const reservations            = useSelector((s: RootState) => s.shortstayReservations.listingReservations);
  const listingRequests         = useSelector((s: RootState) => s.shortstayReservations.listingReservationRequests);
  const listingAvailability     = useSelector((s: RootState) => s.shortstayReservations.listingAvailability);
const hourAvailability = useSelector((s: RootState) => s.shortstayReservations.hourAvailability);
  const credits             = useSelector((s: RootState) => s.payouts.credits);
  const user                = useSelector((s: RootState) => s.auth.user);
  const userId              = user?.userId;

  const { available } = listingAvailability || { available: true, conflict: null };
const { convertRaw, symbol } = useCurrency();
  /* ── responsive ── */
  const [isDesktop, setIsDesktop] = useState(typeof window !== "undefined" && window.innerWidth >= DESKTOP_MIN);
  const isMobile = !isDesktop;
  useEffect(() => {
    const r = () => setIsDesktop(window.innerWidth >= DESKTOP_MIN);
    window.addEventListener("resize", r);
    return () => window.removeEventListener("resize", r);
  }, []);

  /* ── scroll ── */
  const [scrolled, setScrolled]   = useState(false);
  const reserveBtnRef              = useRef<HTMLButtonElement | null>(null);
  const prevCheckOutRef            = useRef<string | null>(null);
  const missingRefs                = useRef<Record<string, HTMLDivElement | null>>({});
  const loaderRef                  = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);

  /* ── data loading ── */
  useEffect(() => {
    if (!listingId) return;
    const u = dispatch(listenToShortstayData(listingId));
    return () => { if (typeof u === "function") u(); };
  }, [dispatch, listingId]);

useEffect(() => {
    if (listingId) {
      dispatch(listenToListingReservations(listingId));
      dispatch(listenToListingReservationRequests(listingId));
      dispatch(fetchShortRatingsByListing(listingId));
    }
    if (userId) dispatch(listenToUserPayouts(userId));
  }, [dispatch, listingId, userId]);

  /* ── rate type ── */
  const rateType  = listing?.rateType;
  
  const isPerHour  = rateType === "perHour" || rateType === "perHourPerPerson";
  const isPerWeek  = rateType === "perWeek";
  const isPerMonth = rateType === "perMonth";
  const isPerYear  = rateType === "perYear";

  const maxNights    = listing?.maxNights ?? 365;
  // unlock tabs based on rate type too
  const canUseFlex   = isPerWeek  || isPerMonth || isPerYear || maxNights >= 7;
  const canUseMonths = isPerMonth || isPerYear  || maxNights >= 30;
  const canUseYearly = isPerYear  || maxNights >= 365;

  /* ── calendar state ── */
  const [tab, setTab]               = useState<"dates"|"months"|"flex"|"yearly">("dates");
  const [checkIn, setCheckIn]       = useState<string|null>(null);
  const [checkOut, setCheckOut]     = useState<string|null>(null);
  const [cursor, setCursor]         = useState(dayjs());
  const [mobileCursor, setMobileCursor] = useState(dayjs().startOf("month"));
  const [flexSelected, setFlexSelected] = useState<"weekend"|"week"|"month"|null>(null);
  const [yearDuration, setYearDuration] = useState(1);

const [unitCount, setUnitCount] = useState(1); // shared by perWeek + perMonth extend logic
  const [monthsToShow, setMonthsToShow] = useState(6);
  const [selectedMonth, setSelectedMonth] = useState<number|null>(null);
  const MAX_YEARS = 10;

  /* ── guests ── */
  const maxGuests = Number(listing?.guestCount || 1);
  const [guestCount, setGuestCount] = useState({ adults: 1, children: 0, infants: 0, pets: 0 });
  const totalGuests = guestCount.adults + guestCount.children + guestCount.infants + guestCount.pets;

  /* ── extras ── */
  const [selectedExtras, setSelectedExtras] = useState<string[]>([]);
  const [selectedTimes, setSelectedTimes]   = useState<string[]>([]);

  /* ── ui state ── */
  const [warningMsg, setWarningMsg]           = useState<string|null>(null);
  const [missingItems, setMissingItems]       = useState<string[]>([]);
  const [showMissingModal, setShowMissingModal] = useState(false);
  const [showGuestModal, setShowGuestModal]   = useState(false);
  const [showHoursModal, setShowHoursModal]   = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string,boolean>>({});
const [showAllAmenities, setShowAllAmenities] = useState(false);
const [visibleAmenityCount, setVisibleAmenityCount] = useState(12);
  const [galleryOpen, setGalleryOpen]         = useState(false);
  const [galleryIdx, setGalleryIdx]           = useState(0);
  const [galleryGridOpen, setGalleryGridOpen] = useState(false);
  const [likedListing, setLikedListing]       = useState(false);
  const [shareToast, setShareToast]           = useState(false);
const [selectedReview, setSelectedReview]   = useState<string|null>(null);
const [descModalOpen, setDescModalOpen]     = useState(false);
  const photos = listing?.photos ?? [];

  /* ── auto-set today/tomorrow ── */
  useEffect(() => {
    if (!listing) return;
    if (checkIn && checkOut) return; // already set by search params

    const start = dayjs().add(1, "day");

    if (isPerYear) {
      // default: 1 year from tomorrow
      setCheckIn(start.format("YYYY-MM-DD"));
      setCheckOut(start.add(1, "year").format("YYYY-MM-DD"));
      setTab("yearly");
    } else if (isPerMonth) {
      // default: 1 month from tomorrow
      setCheckIn(start.format("YYYY-MM-DD"));
      setCheckOut(start.add(1, "month").format("YYYY-MM-DD"));
      setTab("months");
    } else if (isPerWeek) {
      // default: 7 days from tomorrow
      setCheckIn(start.format("YYYY-MM-DD"));
      setCheckOut(start.add(7, "day").format("YYYY-MM-DD"));
      setTab("flex");
    } else {
      // perNight / perHour — just today → tomorrow
      setCheckIn(dayjs().format("YYYY-MM-DD"));
      setCheckOut(dayjs().add(1, "day").format("YYYY-MM-DD"));
    }
  }, [listing?.rateType]); // re-run when listing loads

  /* ── search params ── */
  useEffect(() => {
    const ci = searchParams.get("checkIn");
    const co = searchParams.get("checkOut");
    if (ci && co) { setCheckIn(ci); setCheckOut(co); }
    const adults = Number(searchParams.get("guests"));
    if (adults) setGuestCount(p => ({...p, adults}));
  }, []);

  /* ── auto-select cleaning extra ── */
  useEffect(() => {
    if (!listing?.extras) return;
    const cleaning = Object.keys(listing.extras).find(n => n.toLowerCase().includes("clean"));
    if (cleaning) setSelectedExtras([cleaning]);
  }, [listing]);

  /* ── availability ── */
  useEffect(() => {
    if (!listingId || !checkIn || !checkOut) return;
    const u = dispatch(listenToListingAvailability(listingId, checkIn, checkOut));
    return () => { if (u) u(); };
  }, [dispatch, listingId, checkIn, checkOut]);

  useEffect(() => {
    if (!isPerHour || !listingId || !checkIn) return;
    const u = dispatch(listenToListingAvailabilityByHours({ listingId, date: checkIn }));
    return () => { if (typeof u === "function") u(); };
  }, [dispatch, isPerHour, listingId, checkIn]);


  /* ── warning auto-clear ── */
  useEffect(() => {
    if (!warningMsg) return;
    const t = setTimeout(() => setWarningMsg(null), 3000);
    return () => clearTimeout(t);
  }, [warningMsg]);

  /* ── share toast ── */
  useEffect(() => {
    if (!shareToast) return;
    const t = setTimeout(() => setShareToast(false), 2000);
    return () => clearTimeout(t);
  }, [shareToast]);

  /* ── scroll to reserve after checkout selected ── */
  useEffect(() => {
    if (!isDesktop) return;
    if (checkOut && prevCheckOutRef.current !== checkOut && reserveBtnRef.current) {
      const timer = setTimeout(() => {
        reserveBtnRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 800);
      return () => clearTimeout(timer);
    }
    prevCheckOutRef.current = checkOut;
  }, [checkOut, isDesktop]);

  /* ── mobile infinite scroll ── */
  useEffect(() => {
    if (!loaderRef.current || !isMobile) return;
    const obs = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && monthsToShow < 12) setMonthsToShow(p => p + 2);
    }, { threshold: 1 });
    obs.observe(loaderRef.current);
    return () => obs.disconnect();
  }, [monthsToShow, isMobile]);

  /* ── reserved dates set ── */
  const reservedDates = useMemo(() => {
    const s = new Set<string>();
    reservations.filter(r => !r.isCancelled).forEach(r => {
      let d = dayjs(r.checkIn);
      while (d.isBefore(dayjs(r.checkOut), "day")) {
        s.add(d.format("YYYY-MM-DD"));
        d = d.add(1, "day");
      }
    });
    listing?.unavailableDates?.forEach((d: string) => s.add(d));
    return s;
  }, [reservations, listing]);

  const unavailableDates = reservedDates;
  const today = dayjs();

  /* ── derived facts ── */
  const facts = useMemo(() => ({
    title:        listing?.title ?? "",
    guests:       listing?.peopleAllowed ?? listing?.guestCount ?? 0,
    guestCount:   listing?.guestCount ?? listing?.peopleAllowed ?? 0,
    bedrooms:     listing?.bedrooms ?? 0,
    beds:         listing?.beds ?? 0,
    baths:        listing?.bathrooms ?? 0,
    parkingLots:  listing?.parkingLots ?? 0,
    squareMeters: listing?.squareMeters ?? 0,
    discounts:    listing?.discounts ?? null,
  }), [listing]);

  const hostUser = useMemo(() => {
    if (!listing?.hostId) return null;
    return allUsers.find((u: any) => u.userId === listing.hostId);
  }, [listing?.hostId, allUsers]);

  const host = useMemo(() => ({
    hostId:    listing?.hostId,
    firstName: listing?.hostFirstName ?? "Host",
  }), [listing]);

  const hostReviewsCount = shortRatings.length;
  const hostAvgRating = useMemo(() => {
    if (!shortRatings.length) return 0;
    return shortRatings.reduce((s: number, r: any) => s + (r.listingRating || 0), 0) / shortRatings.length;
  }, [shortRatings]);

  const avgRating = hostReviewsCount === 0 ? null : Number(hostAvgRating.toFixed(1));
  const reviewCount = hostReviewsCount;
  const top5Ratings = shortRatings.slice(0, 5);

  const isHostBookingOwnListing = listing?.hostId === userId;
const managers = useSelector((s: RootState) => s.auth.managers as string[] | undefined);
const isSystemListing = !!listing?.hostId && Array.isArray(managers) && managers.includes(listing.hostId);

  const rateLabel = useMemo(() => {
    if (!listing?.rate) return "";
const suffix = listing.rateType === "perHour" ? "/ hr"
  : listing.rateType === "perHourPerPerson" ? "/ hr · person"
  : listing.rateType === "perNightPerPerson" ? "/ person · night"
  : listing.rateType === "perWeek" ? "/ week"
  : listing.rateType === "perMonth" ? "/ month"
  : listing.rateType === "perYear" ? "/ year"
  : "/ night";
return `${symbol}${Number(convertRaw(listing.rate, "EUR")).toLocaleString()} ${suffix}`;
}, [listing, symbol, convertRaw]);
  const cancellationPolicy = listing?.cancellationPolicy;
  const cancellationLabel = Object.keys(cancellationPolicy || {}).find(k => cancellationPolicy[k]);
  const formattedPolicy = cancellationLabel
    ? cancellationLabel.charAt(0).toUpperCase() + cancellationLabel.slice(1)
    : null;

  const formatPlaceType = (type?: string) => {
    if (!type) return "";
    return type.replace(/_/g," ").replace(/\b\w/g, l => l.toUpperCase());
  };

  const roomTypeLabel = useMemo(() => {
    if (!listing) return "";
    const place = formatPlaceType(listing.placeType);
    const city = listing.address?.city || listing.address?.county || "Unknown";
    const country = listing.address?.country || "";
    return `${place} in ${city}${country ? `, ${country}` : ""}`;
  }, [listing]);

  /* ── guest favourite detection ── */
/* ── guest favourite detection ── */
const summaries     = useSelector((s: RootState) => s.shortRatings.summaries);
const listingSummary = summaries?.[listingId ?? ""];

const isGuestFavourite = listingSummary
  ? listingSummary.totalReviews >= 5 &&
    listingSummary.avgRating     >= 4.5 &&
    listingSummary.cleanliness   >= 4.5 &&
    listingSummary.location      >= 4.5 &&
    listingSummary.communication >= 4.5 &&
    listingSummary.accuracy      >= 4.5 &&
    listingSummary.service       >= 4.5 &&
    listingSummary.value         >= 4.5
  : listing?.guestFavourite === true;   // fallback to flag if summary not loaded yet

const isHighlyRated = !isGuestFavourite &&
  hostReviewsCount >= 10 &&
  hostAvgRating >= 4.5;

  
  /* ── date helpers ── */
  const isSelected = (d: dayjs.Dayjs) => {
    const k = d.format("YYYY-MM-DD");
    if (!checkIn) return false;
    if (checkIn && !checkOut) return k === checkIn;
    return checkIn && checkOut && dayjs(k).isAfter(checkIn) && dayjs(k).isBefore(checkOut);
  };
  const isEdge = (d: dayjs.Dayjs) =>
    d.format("YYYY-MM-DD") === checkIn || d.format("YYYY-MM-DD") === checkOut;

  const handleDateSelect = (date: dayjs.Dayjs) => {
    if (isPerHour) {
      const d = date.format("YYYY-MM-DD");
      setCheckIn(d); setCheckOut(d); return;
    }

    

 

   
  
  
  
  // ── perWeek / perMonth / perYear → snap checkout automatically ──
    // helper: check no reserved dates fall inside a range
    const rangeHasConflict = (from: dayjs.Dayjs, to: dayjs.Dayjs): boolean => {
      let c = from.add(1, "day");
      while (c.isBefore(to, "day")) {
        if (reservedDates.has(c.format("YYYY-MM-DD"))) return true;
        c = c.add(1, "day");
      }
      return false;
    };

    if (isPerWeek || isPerMonth || isPerYear) {
      const unit = isPerWeek ? "week" : isPerMonth ? "month" : "year";
      const unitLabel = unit === "week" ? "week" : unit === "month" ? "month" : "year";
      const maxUnits = Math.max(
        1,
        unit === "week" ? Math.floor(maxNights / 7)
        : unit === "month" ? Math.floor(maxNights / 30)
        : Math.floor(maxNights / 365)
      );

      const d = date.format("YYYY-MM-DD");

      // no existing range, or clicked a date at/before current checkIn → start fresh, 1 unit
      if (!checkIn || !checkOut || date.isBefore(dayjs(checkIn), "day") || date.isSame(dayjs(checkIn), "day")) {
        if (date.isBefore(today, "day") || reservedDates.has(d)) return;
        const end = date.add(1, unit);
        if (rangeHasConflict(date, end)) {
          setWarningMsg(`A reserved date falls within this ${unitLabel}. Please pick another start date.`);
          return;
        }
        setUnitCount(1);
        setCheckIn(d);
        setCheckOut(end.format("YYYY-MM-DD"));
        return;
      }

      // clicked after current checkOut → extend in whole-unit steps until it covers the clicked date
      if (date.isAfter(dayjs(checkOut), "day")) {
        let units = Math.max(1, Math.round(dayjs(checkOut).diff(dayjs(checkIn), unit, true)));
        let end = dayjs(checkIn).add(units, unit);

        while (end.isBefore(date) && units < maxUnits) {
          units += 1;
          end = dayjs(checkIn).add(units, unit);
        }

        if (end.isBefore(date)) {
          setWarningMsg(`Max stay is ${maxUnits} ${unitLabel}${maxUnits > 1 ? "s" : ""}.`);
          return;
        }
        if (rangeHasConflict(dayjs(checkIn), end)) {
          setWarningMsg(`A reserved date falls within the extended range. Please pick another date.`);
          return;
        }
        setUnitCount(units);
        if (unit === "year") setYearDuration(units);
        setCheckOut(end.format("YYYY-MM-DD"));
        return;
      }

  // clicked inside the current range (before checkOut, after checkIn) → shrink by whole units
      if (date.isAfter(dayjs(checkIn), "day") && date.isBefore(dayjs(checkOut), "day")) {
        let units = Math.max(1, Math.round(dayjs(checkOut).diff(dayjs(checkIn), unit, true)));
        let end = dayjs(checkIn).add(units, unit);

        // shrink one unit at a time as long as we're still covering the clicked date and stay above 1 unit
        while (units > 1) {
          const shrunk = units - 1;
          const shrunkEnd = dayjs(checkIn).add(shrunk, unit);
          if (shrunkEnd.isBefore(date) || shrunkEnd.isSame(date, "day")) break;
          units = shrunk;
          end = shrunkEnd;
        }

        if (rangeHasConflict(dayjs(checkIn), end)) {
          setWarningMsg(`A reserved date falls within the reduced range. Please pick another date.`);
          return;
        }
        setUnitCount(units);
        if (unit === "year") setYearDuration(units);
        setCheckOut(end.format("YYYY-MM-DD"));
        return;
      }

      // clicked exactly on checkOut, or anything else → no-op
      return;
    }
  

    const dateStr = date.format("YYYY-MM-DD");
    if (date.isBefore(today,"day")) return;
    if (reservedDates.has(dateStr)) return;
    const minN = listing?.minNights || 1;
    const maxN = listing?.maxNights || 30;

    if (!checkIn || date.isBefore(dayjs(checkIn),"day")) {
      const autoOut = dayjs(dateStr).add(minN,"day");
      if (autoOut.isAfter(dayjs(dateStr).add(maxN,"day"))) { setWarningMsg(`Min stay ${minN}, max ${maxN}`); return; }
      let c = dayjs(dateStr).add(1,"day");
      while (c.isBefore(autoOut)) { if (reservedDates.has(c.format("YYYY-MM-DD"))) { setWarningMsg("Range includes reserved dates."); return; } c = c.add(1,"day"); }
      setCheckIn(dateStr); setCheckOut(autoOut.format("YYYY-MM-DD")); return;
    }
    if (checkIn && checkOut) {
      if (dateStr === checkIn) { setCheckIn(null); setCheckOut(null); return; }
      if (date.isAfter(dayjs(checkIn),"day") && date.isBefore(dayjs(checkOut),"day")) {
        const nights = date.diff(dayjs(checkIn),"day");
        if (nights < minN) { setWarningMsg(`Min stay ${minN} nights.`); return; }
        setCheckOut(dateStr); return;
      }
      if (date.isAfter(dayjs(checkOut),"day")) {
        const nights = date.diff(dayjs(checkIn),"day");
        if (nights > maxN) { setWarningMsg(`Max stay ${maxN} nights.`); return; }
        let c = dayjs(checkOut);
        while (c.isBefore(date)) { if (reservedDates.has(c.format("YYYY-MM-DD"))) { setWarningMsg("Extension hits a reserved date."); return; } c = c.add(1,"day"); }
        setCheckOut(dateStr); return;
      }
    }
    if (checkIn && !checkOut) {
      if (date.isBefore(dayjs(checkIn))) { setCheckIn(dateStr); return; }
      const nights = date.diff(dayjs(checkIn),"day");
      if (nights < minN) { setWarningMsg(`Min stay ${minN} nights.`); return; }
      if (nights > maxN) { setWarningMsg(`Max stay ${maxN} nights.`); return; }
      let c = dayjs(checkIn).add(1,"day");
      while (c.isBefore(date)) { if (reservedDates.has(c.format("YYYY-MM-DD"))) { setWarningMsg("Range includes reserved dates."); return; } c = c.add(1,"day"); }
      setCheckOut(dateStr);
    }
  };




  const handleDateDoubleClick = (date: dayjs.Dayjs) => {
  if (isPerHour) return; // not applicable

  const d = date.format("YYYY-MM-DD");
  if (date.isBefore(today, "day") || reservedDates.has(d)) return;

  const rangeHasConflict = (from: dayjs.Dayjs, to: dayjs.Dayjs): boolean => {
    let c = from.add(1, "day");
    while (c.isBefore(to, "day")) {
      if (reservedDates.has(c.format("YYYY-MM-DD"))) return true;
      c = c.add(1, "day");
    }
    return false;
  };

  if (isPerWeek || isPerMonth || isPerYear) {
    const unit = isPerWeek ? "week" : isPerMonth ? "month" : "year";
    const unitLabel = unit;

    // keep the same duration the user already had, default 1 if none set
    const units = checkIn && checkOut
      ? Math.max(1, Math.round(dayjs(checkOut).diff(dayjs(checkIn), unit, true)))
      : 1;

    const end = date.add(units, unit);
    if (rangeHasConflict(date, end)) {
      setWarningMsg(`A reserved date falls within this ${unitLabel}. Please pick another start date.`);
      return;
    }
    setUnitCount(units);
    if (unit === "year") setYearDuration(units);
    setCheckIn(d);
    setCheckOut(end.format("YYYY-MM-DD"));
    return;
  }

  // perNight: keep same night-count, re-anchor checkIn to the double-clicked date
  const nights = checkIn && checkOut ? dayjs(checkOut).diff(dayjs(checkIn), "day") : (listing?.minNights || 1);
  const end = date.add(nights, "day");
  if (rangeHasConflict(date, end)) {
    setWarningMsg("Range includes reserved dates.");
    return;
  }
  setCheckIn(d);
  setCheckOut(end.format("YYYY-MM-DD"));
};

  const handleGuestChange = (key: keyof typeof guestCount, delta: number) => {
    setGuestCount(prev => {
      let v = prev[key] + delta;
      if (key === "adults") v = Math.max(1, Math.min(maxGuests, v));
      else v = Math.max(0, v);
      return { ...prev, [key]: v };
    });
  };

  const toggleExtra = (name: string) => {
    if (name.toLowerCase().includes("clean")) return;
    setSelectedExtras(prev => prev.includes(name) ? prev.filter(e => e !== name) : [...prev, name]);
  };

  const HOURS_24 = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2,"0")}:00`);
  const selectedIndexes = selectedTimes.map(t => HOURS_24.indexOf(t)).sort((a,b) => a-b);
  const minSel = selectedIndexes[0];
  const maxSel = selectedIndexes[selectedIndexes.length - 1];

  const handleHourClick = (hour: string, index: number, disabled: boolean) => {
    if (disabled) return;
    if (selectedTimes.length === 0) { setSelectedTimes([hour]); return; }
    if (index === minSel - 1) { setSelectedTimes([hour, ...selectedTimes]); return; }
    if (index === maxSel + 1) { setSelectedTimes([...selectedTimes, hour]); return; }
    if (index >= minSel && index <= maxSel) { setSelectedTimes(HOURS_24.slice(minSel, index+1)); return; }
    setWarningMsg("Please select consecutive hours.");
  };

  const getMissingRequirements = () => {
    const m: string[] = [];
    if (!user) m.push("Sign in or create an account");
    if (!checkIn) m.push("Select a check-in date");
    if (!isPerHour && !checkOut) m.push("Select a check-out date");
    if (isPerHour && selectedTimes.length === 0) m.push("Select booking hours");
    if (!guestCount.adults || guestCount.adults < 1) m.push("Add at least 1 adult");
    if (!available) m.push("Selected dates are no longer available");
    if (isHostBookingOwnListing) m.push("You can't book your own listing");
    return m;
  };



const isApproveMode = listing?.bookingType?.approveReservations === true;

// Replace the existing alreadyRequested block
const myRequest = useMemo(() => {
  if (!isApproveMode || !userId) return null;

  const req = listingRequests.find((r: any) => r.guestId === userId);
  if (!req) return null;

  // if the checkout date has passed → treat as expired, reset to null
  if (req.checkOut) {
    const checkOutDate = dayjs(req.checkOut);
    if (checkOutDate.isBefore(dayjs(), "day")) return null;
  }

  return req;
}, [isApproveMode, userId, listingRequests]);

const alreadyRequested = !!myRequest && myRequest.isPending && !myRequest.isApproved && !myRequest.isRejected;
const requestApproved  = !!myRequest && myRequest.isApproved === true;
const requestDeclined  = !!myRequest && myRequest.isRejected === true;

// Replace the existing reserveBtnLabel block
const reserveBtnLabel = !user
  ? "Sign in to Reserve"
  : requestDeclined
  ? "Request Declined"
  : requestApproved
  ? "Reserve"                      // approved → normal reserve flow
  : alreadyRequested
  ? "Already Requested"
  : isApproveMode
  ? "Request to Book"
  : "Reserve";

const handleContinue = async () => {
  if (!user) { navigate("/AuthSwitch"); return; }
  if (!available) { setWarningMsg("These dates are no longer available."); return; }
  if (isHostBookingOwnListing) { setWarningMsg("You cannot book your own listing."); return; }
  const missing = getMissingRequirements();
  if (missing.length > 0) { setMissingItems(missing); setShowMissingModal(true); return; }
  if (isPerHour && selectedTimes.length === 0) { setWarningMsg("Please select at least one hour."); return; }
  if (!listingId || !checkIn || !checkOut) return;

  const reservedDuration = dayjs(checkOut).diff(dayjs(checkIn), "day");

  // Approved request OR non-approve listing → go straight to preview
  if (!isApproveMode || requestApproved) {
    navigate(`/shortaccomodationpreview/${userId}/${listingId}?${new URLSearchParams({
      checkIn,
      checkOut: isPerHour ? checkIn : checkOut,
      reservedDuration: reservedDuration.toString(),
      adults:           guestCount.adults.toString(),
      children:         guestCount.children.toString(),
      infants:          guestCount.infants.toString(),
      pets:             guestCount.pets.toString(),
      selectedExtras:   JSON.stringify(selectedExtras),
      discount:         JSON.stringify(listing?.discounts || {}),
      credits:          credits?.toString() || "0",
      rateType:         rateType || "",
      selectedTimes:    JSON.stringify(selectedTimes),
      totalHours:       selectedTimes.length.toString(),
    }).toString()}`);
    return;
  }

  // Approve mode, no prior request → send request to host
  if (!userId || !listing?.hostId) return;
  try {
const result = await dispatch(upsertShortstayRequest({
      listingId,
      guestId:          userId,
      hostId:           listing.hostId,
      checkIn,
      checkOut,
      adults:           guestCount.adults,
      children:         guestCount.children,
      infants:          guestCount.infants,
      pets:             guestCount.pets,
      selectedExtras,
      selectedTimes,
      totalHours:       selectedTimes.length,
      reservedDuration: dayjs(checkOut).diff(dayjs(checkIn), "day"),
      rateType:         rateType ?? "",
    })).unwrap();

navigate(
  `/imessages/${makeThreadId(userId, listing.hostId)}?${new URLSearchParams({
        requestId:    result.requestId,
        listingId,
        listingTitle: listing.title || "",
        checkIn,
        checkOut,
        nights:       reservedDuration.toString(),
        guests:       (guestCount.adults + guestCount.children).toString(),
        rateType:     rateType || "",
        rate:         listing.rate?.toString() || "",
        requestType:  "shortstayRequest",
      }).toString()}`
    );
  } catch (err) {
    console.error("Request booking error:", err);
    setWarningMsg("Failed to send request. Please try again.");
  }
};

  const toggleSection = (key: string) =>
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  /* ─────────────────────────────────────────────────
     INLINE SUB-COMPONENTS
  ───────────────────────────────────────────────── */

  /* GALLERY LIGHTBOX */
  const GalleryLightbox = () => {
    if (!galleryOpen) return null;
    return (
      <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.96)",zIndex:9000,display:"flex",alignItems:"center",justifyContent:"center" }}>
        <button onClick={() => setGalleryOpen(false)} style={{ position:"absolute",top:20,right:24,background:"transparent",border:`1px solid ${T.graphite}`,color:T.pearl,borderRadius:999,padding:"6px 14px",fontSize:13,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:6 }}>
          <X size={14}/> Close
        </button>
        <button onClick={() => setGalleryIdx(i => (i-1+photos.length)%photos.length)} style={{ position:"absolute",left:20,top:"50%",transform:"translateY(-50%)",background:T.graphite,border:`1px solid ${T.pewter}`,color:T.pearl,borderRadius:"50%",width:44,height:44,display:"grid",placeItems:"center",cursor:"pointer" }}>
          <ChevronLeft size={20}/>
        </button>
        <img src={photos[galleryIdx]} alt="" style={{ maxWidth:"85vw",maxHeight:"85vh",objectFit:"contain",borderRadius:8 }}/>
        <button onClick={() => setGalleryIdx(i => (i+1)%photos.length)} style={{ position:"absolute",right:20,top:"50%",transform:"translateY(-50%)",background:T.graphite,border:`1px solid ${T.pewter}`,color:T.pearl,borderRadius:"50%",width:44,height:44,display:"grid",placeItems:"center",cursor:"pointer" }}>
          <ChevronRight size={20}/>
        </button>
        <div style={{ position:"absolute",bottom:24,left:"50%",transform:"translateX(-50%)",color:T.silver,fontSize:13,fontWeight:600 }}>
          {galleryIdx+1} / {photos.length}
        </div>
        <div style={{ position:"absolute",bottom:52,left:"50%",transform:"translateX(-50%)",display:"flex",gap:6 }}>
          {photos.slice(0,8).map((_,i) => (
            <div key={i} onClick={() => setGalleryIdx(i)} style={{ width:6,height:6,borderRadius:"50%",background:i===galleryIdx?T.gold:T.pewter,cursor:"pointer",transition:"background .2s" }}/>
          ))}
        </div>
      </div>
    );
  };


  /* GALLERY GRID — Airbnb-style photo tour */
/* GALLERY GRID — Airbnb-style sectioned photo tour */
const GalleryGrid = () => {
  if (!galleryGridOpen) return null;

  // chunk photos into groups of 3 for visual sectioning
  const groups: string[][] = [];
  for (let i = 0; i < photos.length; i += 3) groups.push(photos.slice(i, i + 3));

  return (
    <div style={{ position:"fixed", inset:0, background:T.white, zIndex:8500, overflowY:"auto" }}>
      <div style={{ position:"sticky", top:0, background:T.white, zIndex:10, padding:"18px 24px", display:"flex", alignItems:"center", justifyContent:"space-between", borderBottom:`1px solid ${T.pearl}` }}>
        <button onClick={()=>setGalleryGridOpen(false)} style={{ display:"flex",alignItems:"center",gap:6,background:"transparent",border:`1px solid ${T.pearl}`,borderRadius:999,padding:"8px 14px",fontSize:13,fontWeight:600,cursor:"pointer",color:T.black }}>
          <ArrowLeft size={14}/> Back
        </button>
        <div style={{ fontFamily:T.serif, fontWeight:700, fontSize:18, color:T.black }}>Photo tour</div>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={()=>{navigator.clipboard?.writeText(window.location.href);setShareToast(true);}} style={S.headerIconBtn}>
            <Share2 size={16}/>
            <span style={{ fontSize:12,fontWeight:600 }}>Share</span>
          </button>
          <button onClick={()=>setLikedListing(v=>!v)} style={{ ...S.headerIconBtn,color:likedListing?T.red:T.black }}>
            <Heart size={16} fill={likedListing?T.red:"none"} color={likedListing?T.red:T.black}/>
            <span style={{ fontSize:12,fontWeight:600 }}>Save</span>
          </button>
        </div>
      </div>

      <div style={{ maxWidth:1000, margin:"0 auto", padding: isDesktop ? "32px 24px 80px" : "20px 16px 60px" }}>
        <h1 style={{ fontSize: isDesktop?28:22, fontWeight:800, fontFamily:T.serif, color:T.black, marginBottom:36 }}>
          {facts.title}
        </h1>

        {groups.map((group, gi) => {
          // find original indices for this group, for lightbox jump
          const baseIdx = gi * 3;
          return (
            <div key={gi} style={{ marginBottom: 48 }}>
              {isDesktop ? (
                <div style={{ display:"grid", gridTemplateColumns: group.length === 1 ? "1fr" : "1.4fr 1fr", gap:8 }}>
                  {/* big hero */}
                  <div
                    onClick={() => { setGalleryIdx(baseIdx); setGalleryOpen(true); }}
                    style={{ borderRadius:14, overflow:"hidden", cursor:"pointer", background:T.offwhite }}
                  >
                    <img
                      src={group[0]}
                      alt=""
                      style={{ width:"100%", height:420, objectFit:"cover", display:"block", transition:"transform .3s" }}
                      onMouseEnter={e=>(e.currentTarget.style.transform="scale(1.02)")}
                      onMouseLeave={e=>(e.currentTarget.style.transform="scale(1)")}
                    />
                  </div>
                  {/* stacked pair, if present */}
                  {group.length > 1 && (
                    <div style={{ display:"grid", gridTemplateRows: group.length === 3 ? "1fr 1fr" : "1fr", gap:8 }}>
                      {group.slice(1).map((src, si) => (
                        <div
                          key={si}
                          onClick={() => { setGalleryIdx(baseIdx + si + 1); setGalleryOpen(true); }}
                          style={{ borderRadius:14, overflow:"hidden", cursor:"pointer", background:T.offwhite }}
                        >
                          <img
                            src={src}
                            alt=""
                            style={{ width:"100%", height: group.length === 3 ? 206 : 420, objectFit:"cover", display:"block", transition:"transform .3s" }}
                            onMouseEnter={e=>(e.currentTarget.style.transform="scale(1.02)")}
                            onMouseLeave={e=>(e.currentTarget.style.transform="scale(1)")}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                /* mobile: single column, but grouped with spacing as a "section" */
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {group.map((src, si) => (
                    <div
                      key={si}
                      onClick={() => { setGalleryIdx(baseIdx + si); setGalleryOpen(true); }}
                      style={{ borderRadius:14, overflow:"hidden", cursor:"pointer", background:T.offwhite }}
                    >
                      <img src={src} alt="" style={{ width:"100%", height:240, objectFit:"cover", display:"block" }}/>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

  /* AMENITY SECTION */

  // 🖼️ IMAGE TOGGLE — controls whether amenity images render anywhere below.
  // To HIDE images and fall back to icons everywhere: comment out the line below.
  // To SHOW images again: uncomment it.
  const SHOW_AMENITY_IMAGES = false;

  const renderAmenitySection = (
    key: string,
    title: string,
    allItems: CategoryItem[],
    enabledIds: number[] = []
  ) => {
    const expanded = expandedSections[key];
    const visible  = expanded ? allItems : allItems.slice(0, 8);
    const enabled  = allItems.filter(i => enabledIds.includes(i.id));

    const renderIcon = (item: CategoryItem, color: string) => (
      SHOW_AMENITY_IMAGES && item.image ? (
        <img src={item.image} alt={item.name} width={70} height={70} style={{ objectFit:"contain" }} />
      ) : (
        <span style={{ color }}>{item.icon}</span>
      )
    );

    return (
      <section style={S.section}>
        <h2 style={S.sectionTitle}>{title}</h2>
      <style>{`@keyframes amenSpin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
<div style={S.amenGrid}>
  {visible.map(item => {
    const on = enabledIds.includes(item.id);

    if (!on) {
      return (
        <div key={item.id} style={{ ...S.amenCard, opacity:0.32, border:`1px solid ${T.pearl}` }}>
          <div style={{ marginBottom:8, display:"flex",justifyContent:"center" }}>{renderIcon(item, T.silver)}</div>
          <div style={{ fontSize:12,fontWeight:600,color:T.smoke,textDecoration:"line-through",textAlign:"center" }}>{item.name}</div>
        </div>
      );
    }

    return (
      <div key={item.id} style={{
        borderRadius:14, padding:1.5, position:"relative",
        background:"linear-gradient(135deg,#0a0a0a,#bf9b30,#d46b10,#e84020,#bf9b30,#0a0a0a)",
      }}>
        <div style={{ position:"relative", ...S.amenCard, border:"none", borderRadius:12.5 }}>
          <div style={{ marginBottom:8, display:"flex",justifyContent:"center" }}>{renderIcon(item, T.gold)}</div>
          <div style={{ fontSize:12,fontWeight:600,color:T.black,textAlign:"center" }}>{item.name}</div>
        </div>
      </div>
    );
  })}
</div>
        {allItems.length > 8 && (
          <button onClick={() => toggleSection(key)} style={S.showMoreBtn}>
            {expanded ? "Show less" : `Show all ${allItems.length}`}
          </button>
        )}
      </section>
    );
  };

  /* COMBINED AMENITIES — 9-item preview, single global "Show all" + "Show more (12 at a time)" */
  const renderAllAmenitiesSection = () => {
    const categoryDefs: { key: string; title: string; items: CategoryItem[]; enabledIds?: number[] }[] = [
      { key: "amenities",     title: "Amenities",        items: amenitiesCategories,   enabledIds: listing?.amenities },
      { key: "kitchen",       title: "Kitchen",          items: KitchenAmenities,      enabledIds: listing?.kitchenAmenities },
      { key: "laundry",       title: "Laundry",          items: LaundryAmenities,      enabledIds: listing?.laundryAmenities },
      { key: "bedroom",       title: "Bedroom",          items: BedroomAmenities,      enabledIds: listing?.bedroomAmenities },
      { key: "bathroom",      title: "Bathroom",         items: BathroomAmenities,     enabledIds: listing?.bathroomAmenities },
      { key: "entertainment", title: "Entertainment",    items: EntertainmentAmenities,enabledIds: listing?.entertainmentAmenities },
      { key: "children",      title: "Family & Children",items: ChildrenAmenities,     enabledIds: listing?.childrenAmenities },
      { key: "office",        title: "Office",           items: OfficeAmenities,       enabledIds: listing?.officeAmenities },
      { key: "safety",        title: "Safety & Security",items: safetyCategories,      enabledIds: listing?.safety },
      { key: "rules",         title: "House Rules",      items: rulesCategories,       enabledIds: listing?.rules },
    ];

    // Flatten every enabled amenity across every category into one list,
    // tagging each with which category it came from (used only for grouping headers).
    const flatEnabled = categoryDefs.flatMap(cat =>
      (cat.items || [])
        .filter(i => (cat.enabledIds || []).includes(i.id))
        .map(i => ({ ...i, categoryKey: cat.key, categoryTitle: cat.title }))
    );

    const renderIcon = (item: CategoryItem, color: string) => (
      SHOW_AMENITY_IMAGES && item.image ? (
        <img src={item.image} alt={item.name} width={70} height={70} style={{ objectFit:"contain" }} />
      ) : (
        <span style={{ color }}>{item.icon}</span>
      )
    );

    const renderCard = (item: CategoryItem & { categoryKey: string }) => (
      <div key={`${item.categoryKey}-${item.id}`} style={{
        borderRadius:14, padding:1.5, position:"relative",
        background:"linear-gradient(135deg,#0a0a0a,#bf9b30,#d46b10,#e84020,#bf9b30,#0a0a0a)",
      }}>
        <div style={{ position:"relative", ...S.amenCard, border:"none", borderRadius:12.5 }}>
          <div style={{ marginBottom:8, display:"flex",justifyContent:"center" }}>{renderIcon(item, T.gold)}</div>
          <div style={{ fontSize:12,fontWeight:600,color:T.black,textAlign:"center" }}>{item.name}</div>
        </div>
      </div>
    );

    /* ── Collapsed preview ── */
    if (!showAllAmenities) {
      const preview = flatEnabled.slice(0, 9);
      return (
        <section style={S.section}>
          <h2 style={S.sectionTitle}>Amenities</h2>
          <div style={S.amenGrid}>
            {preview.map(renderCard)}
          </div>
          {flatEnabled.length > 9 && (
            <button onClick={() => { setShowAllAmenities(true); setVisibleAmenityCount(12); }} style={S.showMoreBtn}>
              Show all amenities
            </button>
          )}
        </section>
      );
    }

    /* ── Expanded view: grouped by category, paginated 12 at a time globally ── */
    const visibleFlat = flatEnabled.slice(0, visibleAmenityCount);
    const grouped = categoryDefs
      .map(cat => ({
        ...cat,
        visibleItems: visibleFlat.filter(i => i.categoryKey === cat.key),
      }))
      .filter(cat => cat.visibleItems.length > 0);

    const hasMore = visibleAmenityCount < flatEnabled.length;

    return (
      <section style={S.section}>
        <h2 style={S.sectionTitle}>Amenities</h2>

        {grouped.map(cat => (
          <div key={cat.key} style={{ marginBottom:28 }}>
            <h3 style={{ fontSize:15, fontWeight:700, color:T.black, marginBottom:12, letterSpacing:"0.02em" }}>
              {cat.title}
            </h3>
            <div style={S.amenGrid}>
              {cat.visibleItems.map(renderCard)}
            </div>
          </div>
        ))}

        {hasMore ? (
          <button onClick={() => setVisibleAmenityCount(c => c + 12)} style={S.showMoreBtn}>
            Show more ({flatEnabled.length - visibleAmenityCount} more)
          </button>
        ) : (
          <button onClick={() => { setShowAllAmenities(false); setVisibleAmenityCount(12); }} style={S.showMoreBtn}>
            Show less
          </button>
        )}
      </section>
    );
  };

  /* STAT BLOCK */
  const StatBlock = ({ value, label }: { value: string|number; label: string }) => (
    <div style={{ textAlign:"center",padding:"16px 0" }}>
      <div style={{ fontSize:28,fontWeight:800,color:T.black,fontFamily:T.serif }}>{value}</div>
      <div style={{ fontSize:12,color:T.smoke,marginTop:2,letterSpacing:"0.05em",textTransform:"uppercase" }}>{label}</div>
    </div>
  );

  /* RENDER DESCRIPTION */

const isHeadingLine = (p: string) => {
  const trimmed = p.trim();
  if (trimmed.endsWith(":")) return true;
  if (trimmed.length <= 45 && !/[.!?]$/.test(trimmed)) return true;
  if (trimmed === trimmed.toUpperCase() && /[A-Z]/.test(trimmed) && trimmed.length <= 60) return true;
  return false;
};

const renderFullDescription = (text?: string) => {
  if (!text?.trim()) return <p style={{ color:T.smoke,fontSize:15 }}>No description provided.</p>;
  return text.split(/\n+/).map(p => p.trim()).filter(Boolean).map((p,i) => {
    if (isHeadingLine(p)) {
      return (
        <h4 key={i} style={{ fontSize:18, fontWeight:800, color:T.black, marginTop: i===0?0:24, marginBottom:8, fontFamily:T.serif }}>
          {p.replace(/:$/, "")}
        </h4>
      );
    }
    return (
      <p key={i} style={{ marginBottom:18,fontSize:17,lineHeight:1.85,color:T.black,fontFamily:T.serif }}>{p}</p>
    );
  });
};


const renderDescription = (text?: string) => {
  if (!text?.trim()) return <p style={{ color:T.smoke,fontSize:15 }}>No description provided.</p>;
  const paragraphs = text.split(/\n+/).map(p => p.trim()).filter(Boolean);
  const preview = paragraphs.slice(0, 2);
  const hasMore = paragraphs.length > 2 || preview.join(" ").length > 280;
  return (
    <>
      {preview.map((p,i) => (
        <p key={i} style={{ marginBottom:16,fontSize:16,lineHeight:1.8,color:T.black }}>{p}</p>
      ))}
      {hasMore && (
        <button
          onClick={() => setDescModalOpen(true)}
          style={{ background:"transparent", border:"none", padding:0, fontSize:14, fontWeight:700, color:T.black, textDecoration:"underline", cursor:"pointer" }}
        >
          Read more
        </button>
      )}
    </>
  );
};

  const renderAboutMe = (text?: string) => {
    if (!text?.trim()) return <p style={{ color:T.smoke,fontSize:14 }}>This host hasn't added an introduction yet.</p>;
    return text.split(/\n+/).map(p => p.trim()).filter(Boolean).map((p,i) => (
      <p key={i} style={{ marginBottom:14,fontSize:15,lineHeight:1.75,color:T.black }}>{p}</p>
    ));
  };

  /* ── Calendar Month Renderer ── */
  

  const renderCalendarMonth = (baseMonth: dayjs.Dayjs, compact = false) => {
    const start       = baseMonth.startOf("month");
    const daysInMonth = baseMonth.daysInMonth();
    const startDay    = start.day();
    const cellSize    = compact ? 38 : 44;

    const cells = Array.from({ length: startDay + daysInMonth }).map((_, i) => {
      if (i < startDay) return null;
      return start.add(i - startDay, "day");
    });

    return (
      <div key={baseMonth.toString()}>
        <div style={{
          fontWeight: 700, textAlign: "center", marginBottom: 14,
          fontSize: compact ? 15 : 17, color: T.gold,
          fontFamily: T.serif, letterSpacing: "0.04em",
        }}>
          {baseMonth.format("MMMM YYYY")}
        </div>
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(7,1fr)",
          gap: 2, marginBottom: 8, textAlign: "center",
          fontSize: 11, color: T.smoke, fontWeight: 600, letterSpacing: "0.06em",
        }}>
          {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => <div key={d}>{d}</div>)}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: compact ? 4 : 6 }}>
          {cells.map((d, i) => {
            if (!d) return <div key={i} style={{ width: cellSize, height: cellSize }} />;

            const dd        = d.format("YYYY-MM-DD");
            const isPast    = d.isBefore(today, "day");
            const isUnavail = unavailableDates.has(dd);
            const sel       = isSelected(d);
            const edge      = isEdge(d);
            const blocked   = isPast || isUnavail;

            const base3d = `
              0 1px 0 rgba(255,255,255,0.8) inset,
              0 -1px 0 rgba(0,0,0,0.08) inset,
              0 2px 4px rgba(0,0,0,0.10)
            `;
            const edge3d = `
              0 1px 0 rgba(255,255,255,0.5) inset,
              0 -1px 0 rgba(0,0,0,0.18) inset,
              0 4px 12px ${T.gold}55,
              0 1px 3px rgba(0,0,0,0.2)
            `;
            const sel3d = `
              0 1px 0 rgba(255,255,255,0.6) inset,
              0 -1px 0 rgba(0,0,0,0.10) inset,
              0 2px 8px ${T.gold}30
            `;

            return (
              <div
                key={i}
                onClick={() => !blocked && handleDateSelect(d)}
               
onDoubleClick={() => !blocked && handleDateDoubleClick(d)}
                onMouseEnter={e => {
                  if (!blocked && !edge) {
                    (e.currentTarget as HTMLDivElement).style.transform = "translateY(-2px) scale(1.08)";
                    (e.currentTarget as HTMLDivElement).style.boxShadow = `0 1px 0 rgba(255,255,255,0.7) inset, 0 -1px 0 rgba(0,0,0,0.12) inset, 0 6px 16px ${T.gold}40, 0 2px 6px rgba(0,0,0,0.15)`;
                    (e.currentTarget as HTMLDivElement).style.background = T.goldSubtle;
                    (e.currentTarget as HTMLDivElement).style.border = `1px solid ${T.goldBorder}`;
                  }
                }}
                onMouseLeave={e => {
                  if (!blocked && !edge) {
                    (e.currentTarget as HTMLDivElement).style.transform = "translateY(0) scale(1)";
                    (e.currentTarget as HTMLDivElement).style.boxShadow = sel ? sel3d : "none";
                    (e.currentTarget as HTMLDivElement).style.background = sel ? T.goldSubtle : "transparent";
                    (e.currentTarget as HTMLDivElement).style.border = sel ? `1px solid ${T.goldBorder}` : "1px solid transparent";
                  }
                }}
                style={{
                  width: cellSize, height: cellSize,
                  borderRadius: cellSize / 2,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: compact ? 13 : 14,
                  fontWeight: edge ? 800 : 600,
                  cursor: blocked ? "not-allowed" : "pointer",
                  opacity: isUnavail ? 0.35 : 1,
                  background: isPast    ? "transparent"
                    : isUnavail         ? T.pearl
                    : edge              ? GOLD_GRAD
                    : sel               ? T.goldSubtle
                    : "transparent",
                  color: edge       ? T.black
                    : isUnavail     ? T.silver
                    : isPast        ? T.silver
                    : T.black,
                  border: edge      ? `2px solid ${T.gold}`
                    : sel           ? `1px solid ${T.goldBorder}`
                    : "1px solid transparent",
                  boxShadow: edge ? edge3d : sel ? sel3d : "none",
                  textDecoration: isUnavail ? "line-through" : "none",
                  transition: "transform 0.15s cubic-bezier(.34,1.56,.64,1), box-shadow 0.15s ease, background 0.15s ease",
                  transform: "translateY(0) scale(1)",
                  position: "relative",
                }}
              >
                {d.date()}
              </div>
            );
          })}
        </div>
      </div>
    );
  };


  /* ── Months Tab ── */
  const renderMonthsTab = () => (
    <div style={{ display:"grid",gridTemplateColumns:`repeat(${isDesktop?4:3},1fr)`,gap:12 }}>
      {Array.from({length:12}).map((_,i) => {
        const m = today.add(i,"month");
        
    
    
    let hasUnavail = false;
        const rangeStart = isPerMonth
          ? (m.isSame(today,"month") ? today.add(1,"day") : m.startOf("month"))
          : m.startOf("month");
        const rangeEnd = isPerMonth
          ? rangeStart.add(1,"month")
          : m.endOf("month");
        let d = rangeStart.clone();
        while (d.isBefore(rangeEnd,"day")) {
          if (unavailableDates.has(d.format("YYYY-MM-DD"))) { hasUnavail = true; break; }
          d = d.add(1,"day");
        }
    
    
        const isSel = checkIn && checkOut && m.isSame(dayjs(checkIn),"month") && m.isSame(dayjs(checkOut),"month");
        return (
          
          <button key={i} disabled={hasUnavail} onClick={() => {
            let startDate: dayjs.Dayjs;
            let endDate: dayjs.Dayjs;

            if (isPerMonth) {
          
              const isCurrentMonth = m.isSame(today, "month");
              startDate = isCurrentMonth ? today.add(1, "day") : m.startOf("month");
              endDate   = startDate.add(1, "month");
            } else {
              // default: fill calendar month 1st → last
              const isCurrentMonth = m.isSame(today, "month");
              startDate = isCurrentMonth ? today.add(1, "day") : m.startOf("month");
              endDate   = m.endOf("month");
            }

            if (startDate.isAfter(endDate)) return;
            setCheckIn(startDate.format("YYYY-MM-DD")); setCheckOut(endDate.format("YYYY-MM-DD"));
        
        }} style={{ padding:"16px 8px",borderRadius:14,border:`1px solid ${isSel?T.gold:T.pearl}`,fontWeight:700,cursor:hasUnavail?"not-allowed":"pointer",opacity:hasUnavail?.35:1,background:isSel?GOLD_GRAD:T.white,color:isSel?T.black:T.black,transition:"all .2s",boxShadow:isSel?`0 4px 16px ${T.gold}40`:"none" }}>
            <div style={{ fontSize:15,color:isSel?T.black:T.gold,marginBottom:2 }}>{m.format("MMM")}</div>
            <div style={{ fontSize:11,color:isSel?T.black:T.smoke }}>{m.year()}</div>
          </button>
        );
      })}
    </div>
  );

  /* ── Flex Tab ── */
  const renderFlexTab = () => {
    const options = [
      { key:"weekend",label:"Weekend",fn:() => {
        let fri = today.day()>=5 ? today.add(1,"week").day(5) : today.day(5);
        if (fri.isBefore(today,"day")) fri = fri.add(1,"week");
        if (isPerHour) { const d=fri.format("YYYY-MM-DD"); setCheckIn(d); setCheckOut(d); }
        else { setCheckIn(fri.format("YYYY-MM-DD")); setCheckOut(fri.add(2,"day").format("YYYY-MM-DD")); }
        setFlexSelected("weekend");
      }},
      { key:"week",label:"Week",fn:() => {
        const s=today.add(1,"day");
        if (isPerHour){const d=s.format("YYYY-MM-DD");setCheckIn(d);setCheckOut(d);}
        else{setCheckIn(s.format("YYYY-MM-DD"));setCheckOut(s.add(7,"day").format("YYYY-MM-DD"));}
        setFlexSelected("week");
      }},
      { key:"month",label:"Month",fn:() => {
        setCheckIn(today.add(1,"day").format("YYYY-MM-DD"));
        setCheckOut(today.add(30,"day").format("YYYY-MM-DD"));
        setFlexSelected("month");
      }},
    ].filter(x => !(isPerHour && x.key==="month"));

    return (
      <div style={{ display:"flex",gap:12,flexWrap:"wrap" }}>
        {options.map(x => {
          const active = flexSelected === x.key;
          return (
            <button key={x.key} onClick={x.fn} style={{ padding:"12px 24px",borderRadius:999,border:`1px solid ${active?T.gold:T.pearl}`,fontWeight:700,fontSize:14,background:active?GOLD_GRAD:T.white,color:active?T.black:T.black,cursor:"pointer",transition:"all .2s",boxShadow:active?`0 4px 14px ${T.gold}40`:"none" }}>
              {x.label}
            </button>
          );
        })}
      </div>
    );
  };

  /* ── Yearly Tab ── */
  const renderYearlyTab = () => (
    <div>
      <p style={{ fontWeight:600,marginBottom:16,color:T.smoke,fontSize:14 }}>Select number of years:</p>
      <div style={{ display:"flex",alignItems:"center",gap:16,marginBottom:20 }}>
        <button onClick={() => setYearDuration(y=>Math.max(1,y-1))} style={S.counterBtn}>−</button>
        <div style={{ fontSize:24,fontWeight:800,color:T.black,minWidth:60,textAlign:"center",fontFamily:T.serif }}>{yearDuration} yr{yearDuration>1?"s":""}</div>
        <button onClick={() => setYearDuration(y=>Math.min(MAX_YEARS,y+1))} style={S.counterBtn}>+</button>
      </div>
      <button onClick={() => {
        setUnitCount(yearDuration);
        let start = today.add(1,"day").startOf("year");
        const isYearOk = (ys: dayjs.Dayjs) => {
          const ye = ys.endOf("year"); let d = ys.clone();
          while (d.isBefore(ye,"day")) { if (unavailableDates.has(d.format("YYYY-MM-DD"))) return false; d=d.add(1,"day"); }
          return true;
        };
        let attempts=0;
        while (!isYearOk(start) && attempts<MAX_YEARS) { start=start.add(1,"year"); attempts++; }
        if (attempts>=MAX_YEARS) { setWarningMsg("No fully available year found."); return; }
        setCheckIn(start.format("YYYY-MM-DD")); setCheckOut(start.add(yearDuration,"year").format("YYYY-MM-DD"));
      }} style={{ padding:"12px 28px",borderRadius:999,fontWeight:700,background:GOLD_GRAD,border:"none",cursor:"pointer",color:T.black,boxShadow:`0 4px 16px ${T.gold}40` }}>
        Apply {yearDuration} Year{yearDuration>1?"s":""}
      </button>
    </div>
  );

  /* ── Calendar Tabs Bar ── */
  const CalendarTabs = () => (
    <div style={{ display:"flex",gap:8,marginBottom:20,flexWrap:"wrap" }}>
      {(["dates","months","flex","yearly"] as const).map(t => {
        
        
const disabled = (() => {
  // Dates tab is ALWAYS enabled for everyone
  if (t === "dates") return false;

  // perHour → Dates + Flexible only
  if (isPerHour)  return t === "months" || t === "yearly";

  // perWeek → Dates + Flexible only
  if (isPerWeek)  return t === "months" || t === "yearly";

  // perMonth → Dates + Months only
  if (isPerMonth) return t === "flex" || t === "yearly";

  // perYear → Dates + Yearly only
  if (isPerYear)  return t === "flex" || t === "months";

  // perNight / perNightPerPerson → gate by maxNights as before
  return (t === "yearly" && !canUseYearly)
      || (t === "months" && !canUseMonths)
      || (t === "flex"   && !canUseFlex);
})();

        const active = tab===t;
        return (
          <button key={t} disabled={disabled} onClick={() => !disabled && setTab(t)} style={{ padding:"8px 16px",borderRadius:999,border:`1px solid ${active?T.gold:T.pearl}`,fontWeight:700,fontSize:13,background:active?GOLD_GRAD:"transparent",color:active?T.black:T.smoke,cursor:disabled?"not-allowed":"pointer",opacity:disabled?.35:1,transition:"all .2s",letterSpacing:"0.04em" }}>
            {t==="dates"?"Dates":t==="months"?"Months":t==="flex"?"Flexible":"Yearly"}
          </button>
        );
      })}
    </div>
  );

  /* ── Hours & Guests Modal ── */
  const HoursAndGuestsModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    if (!open || !checkIn) return null;
    const isToday    = dayjs(checkIn).isSame(dayjs(),"day");
    const currentHr  = dayjs().hour();

    return (
      <div onClick={onClose} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:5000,backdropFilter:"blur(4px)" }}>
        <div onClick={e=>e.stopPropagation()} style={{ background:T.white,borderRadius:20,padding:28,width:"100%",maxWidth:900,maxHeight:"90vh",overflowY:"auto",boxShadow:"0 30px 80px rgba(0,0,0,.3)",display:"flex",gap:24,flexDirection:isDesktop?"row":"column",position:"relative" }}>
          <button onClick={onClose} style={{ position:"absolute",top:16,right:16,background:T.pearl,border:"none",borderRadius:"50%",width:32,height:32,display:"grid",placeItems:"center",cursor:"pointer" }}><X size={16}/></button>

          {/* HOURS */}
          {isPerHour && (
            <div style={{ flex:1 }}>
              <h3 style={{ fontFamily:T.serif,fontSize:22,fontWeight:700,marginBottom:4,color:T.black }}>Select Hours</h3>
              <p style={{ fontSize:13,color:T.smoke,marginBottom:18 }}>Choose consecutive time slots</p>
              <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10 }}>
                {HOURS_24.map((time,index) => {
                  const hourNum  = Number(time.split(":")[0]);
                  const isPastHr = isToday && hourNum < currentHr;
                  const active   = selectedTimes.includes(time);
                  const disabled = isPastHr || !!hourAvailability?.disabledHours?.includes(hourNum);
                  return (
                    <button key={time} disabled={disabled} onClick={() => !disabled && handleHourClick(time,index,disabled)} style={{ padding:"10px 0",borderRadius:12,border:active?`2px solid ${T.gold}`:`1px solid ${T.pearl}`,background:disabled?T.pearl:active?GOLD_GRAD:T.white,color:disabled?T.silver:T.black,fontWeight:700,fontSize:13,cursor:disabled?"not-allowed":"pointer",opacity:disabled?.4:1,boxShadow:active&&!disabled?`0 0 10px ${T.gold}50`:"none",transition:"all .15s" }}>
                      {time}
                    </button>
                  );
                })}
              </div>
              {selectedTimes.length > 0 && (
                <div style={{ marginTop:14,fontSize:13,fontWeight:700,color:T.gold,padding:"10px 14px",background:T.goldSubtle,borderRadius:10,border:`1px solid ${T.goldBorder}` }}>
                  {selectedTimes.length} hour{selectedTimes.length>1?"s":""}: {selectedTimes[0]} → {selectedTimes[selectedTimes.length-1]}
                </div>
              )}
            </div>
          )}

          {/* GUESTS */}
          <div style={{ flex:1 }}>
            <h3 style={{ fontFamily:T.serif,fontSize:22,fontWeight:700,marginBottom:4,color:T.black }}>Who's Coming?</h3>
            <p style={{ fontSize:13,color:T.smoke,marginBottom:18 }}>Up to {maxGuests} guests</p>
            {([{key:"adults",label:"Adults",sub:"13 or above",min:1},{key:"children",label:"Children",sub:"Ages 2–12",min:0},{key:"infants",label:"Infants",sub:"Under 2",min:0},{key:"pets",label:"Pets",sub:"Service animals",min:0}]as const).map((row,idx,arr)=>{
              const value = guestCount[row.key];
              return (
                <div key={row.key} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 0",borderBottom:idx!==arr.length-1?`1px solid ${T.pearl}`:"none" }}>
                  <div>
                    <div style={{ fontWeight:700,fontSize:15,color:T.black }}>{row.label}</div>
                    <div style={{ fontSize:12,color:T.smoke,marginTop:2 }}>{row.sub}</div>
                  </div>
                  <div style={{ display:"flex",alignItems:"center",gap:14 }}>
                    <button onClick={()=>value>row.min&&handleGuestChange(row.key,-1)} disabled={value<=row.min} style={{ width:34,height:34,borderRadius:"50%",border:`1px solid ${T.pearl}`,background:T.white,fontSize:18,cursor:value<=row.min?"not-allowed":"pointer",color:value<=row.min?T.silver:T.black }}>−</button>
                    <span style={{ minWidth:20,textAlign:"center",fontWeight:700,fontSize:16 }}>{value}</span>
                    <button onClick={()=>handleGuestChange(row.key,1)} disabled={row.key==="adults"&&value>=maxGuests} style={{ width:34,height:34,borderRadius:"50%",border:`1px solid ${T.pearl}`,background:T.white,fontSize:18,cursor:"pointer",color:T.black }}>+</button>
                  </div>
                </div>
              );
            })}

            {checkIn && (
              <div style={{ marginTop:16,padding:"12px 14px",background:T.offwhite,borderRadius:12,fontSize:13,color:T.black,lineHeight:1.7 }}>
                {isPerHour ? (
                  <>
                    <div><strong>Date:</strong> {dayjs(checkIn).format("ddd, DD MMM YYYY")}</div>
                    {selectedTimes.length>0 && <div><strong>Time:</strong> {dayjs(`${checkIn} ${selectedTimes[0]}`).format("h A")} – {dayjs(`${checkIn} ${selectedTimes[0]}`).add(selectedTimes.length,"hour").format("h A")} ({selectedTimes.length}h)</div>}
                  </>
                ) : checkOut && (
                  <>
                    <div><strong>Check-in:</strong> {dayjs(checkIn).format("ddd, DD MMM YYYY")}</div>
                    <div><strong>Check-out:</strong> {dayjs(checkOut).format("ddd, DD MMM YYYY")}</div>
                    <div><strong>Stay:</strong> {dayjs(checkOut).diff(dayjs(checkIn),"day")} night{dayjs(checkOut).diff(dayjs(checkIn),"day")>1?"s":""}</div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ── Missing Modal ── */
  const MissingModal = ({ desktop = false }: { desktop?: boolean }) => {
    if (!showMissingModal) return null;
    return (
      <div onClick={()=>setShowMissingModal(false)} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:desktop?3000:9999,backdropFilter:"blur(3px)" }}>
        <div onClick={e=>e.stopPropagation()} style={{ width:360,background:T.white,borderRadius:20,padding:28,boxShadow:"0 20px 60px rgba(0,0,0,.3)" }}>
          <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:16 }}>
            <AlertCircle size={22} color={T.red}/>
            <h3 style={{ fontFamily:T.serif,fontSize:20,fontWeight:700 }}>Before you continue</h3>
          </div>
          <ul style={{ paddingLeft:18,marginBottom:24 }}>
            {missingItems.map((item,i) => (
              <li key={i} style={{ marginBottom:10,fontSize:14,color:T.black,fontWeight:600,lineHeight:1.5 }}>{item}</li>
            ))}
          </ul>
          <button onClick={()=>setShowMissingModal(false)} style={{ width:"100%",padding:14,borderRadius:12,background:T.red,color:T.white,fontWeight:800,border:"none",cursor:"pointer",fontSize:15 }}>
            Got it
          </button>
        </div>
      </div>
    );
  };


  
  const DescriptionModal = () => {
  if (!descModalOpen) return null;

  return (
    <div
      onClick={() => setDescModalOpen(false)}
      style={{
        position:"fixed", inset:0, background:"rgba(0,0,0,.65)",
        display:"flex",
        alignItems: isDesktop ? "center" : "flex-end",
        justifyContent:"center",
        zIndex:7000, backdropFilter:"blur(4px)",
        padding: isDesktop ? 20 : 0,
      }}
    >
      <style>{`
        @keyframes sheetUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes cardIn  { from { opacity:0; transform: translateY(12px) scale(.98); } to { opacity:1; transform: translateY(0) scale(1); } }
      `}</style>

      <div
        onClick={e => e.stopPropagation()}
        style={isDesktop ? {
          position:"relative",
          width:"100%", maxWidth:560, maxHeight:"82vh", overflowY:"auto",
          background:"#FDFBF6",
          borderRadius:16,
          padding:"40px 44px",
          boxShadow:"0 30px 70px rgba(0,0,0,.45), 0 0 0 1px rgba(0,0,0,.06)",
          fontFamily:T.serif,
          animation:"cardIn .25s ease-out",
        } : {
          position:"relative",
          width:"100%", maxHeight:"85vh", overflowY:"auto",
          background:T.white,
          borderRadius:"20px 20px 0 0",
          padding:"14px 24px 36px",
          boxShadow:"0 -8px 40px rgba(0,0,0,.25)",
          fontFamily:T.serif,
          animation:"sheetUp .28s cubic-bezier(.32,.72,0,1)",
        }}
      >
        {/* drag handle on mobile */}
        {!isDesktop && (
          <div style={{ width:40, height:4, borderRadius:999, background:T.pearl, margin:"4px auto 14px" }}/>
        )}

        {/* book "spine" shadow on desktop only */}
        {isDesktop && (
          <div style={{ position:"absolute", left:0, top:0, bottom:0, width:14, background:"linear-gradient(90deg, rgba(0,0,0,0.10), transparent)" }}/>
        )}

        <button
          onClick={() => setDescModalOpen(false)}
          style={{
            position:"absolute",
            top: isDesktop ? 16 : 12, right: isDesktop ? 16 : 16,
            background:T.pearl, border:"none", borderRadius:"50%",
            width:32, height:32, display:"grid", placeItems:"center", cursor:"pointer",
          }}
        >
          <X size={16}/>
        </button>

        <div style={{ fontSize:11, fontWeight:700, letterSpacing:"0.18em", color:T.gold, textTransform:"uppercase", marginBottom:8 }}>
          About this place
        </div>
        <h3 style={{ fontSize: isDesktop?26:22, fontWeight:800, color:T.black, marginBottom:22, lineHeight:1.25, paddingRight:30 }}>
          {facts.title}
        </h3>
        {renderFullDescription(listing?.description)}
      </div>
    </div>
  );
};


  /* ── Booking Card ── */
  const BookingCard = () => (
    <aside style={S.bookingCard}>
      {/* PRICE HEADER */}
    <div style={{ marginBottom:16 }}>
  <div style={{ display:"flex",alignItems:"baseline",gap:8 }}>
    <span style={{ fontSize:26,fontWeight:800,color:T.black,fontFamily:T.serif }}>
      {listing?.rate ? `${symbol}${convertRaw(listing.rate, "EUR").toLocaleString()}` : "Contact"}
    </span>
 {listing?.rate && (
      <span style={{ fontSize:14,color:T.smoke }}>
        {rateType === "perHour"           ? "/ hour"
        : rateType === "perHourPerPerson" ? "/ hour · person"
        : rateType === "perNightPerPerson"? "/ person · night"
        : rateType === "perWeek"          ? "/ week"
        : rateType === "perMonth"         ? "/ month"
        : rateType === "perYear"          ? "/ year"
        : "/ night"}
      </span>
    )}
  </div>
        {avgRating && (
          <div style={{ display:"flex",alignItems:"center",gap:6,marginTop:4 }}>
            <Star size={13} fill={T.gold} color={T.gold}/>
            <span style={{ fontSize:13,fontWeight:700,color:T.black }}>{avgRating}</span>
            <span style={{ fontSize:13,color:T.smoke }}>· {reviewCount} review{reviewCount!==1?"s":""}</span>
          </div>
        )}
      </div>

      {/* GUEST FAVOURITE BADGE */}
      {(isGuestFavourite || isHighlyRated) && (
        <div style={{ display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:T.goldSubtle,borderRadius:10,border:`1px solid ${T.goldBorder}`,marginBottom:14 }}>
          <Crown size={14} color={T.gold}/>
          <span style={{ fontSize:12,fontWeight:700,color:T.goldDark }}>
            {isGuestFavourite ? "Guest Favourite" : "Highly Rated"}
          </span>
        </div>
      )}

      {/* DATE INPUTS */}
      {checkIn && (
        <div style={{ border:`1px solid ${T.pearl}`,borderRadius:14,overflow:"hidden",marginBottom:14 }}>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr" }}>
            <div style={{ padding:"10px 12px" }}>
              <div style={{ fontSize:10,fontWeight:800,letterSpacing:"0.1em",color:T.smoke }}>CHECK-IN</div>
              <div style={{ fontSize:14,fontWeight:700,marginTop:2 }}>{dayjs(checkIn).format("M/D/YYYY")}</div>
            </div>
            <div style={{ padding:"10px 12px",borderLeft:`1px solid ${T.pearl}` }}>
              
              

              <div style={{ fontSize:10,fontWeight:800,letterSpacing:"0.1em",color:T.smoke }}>
                {isPerHour ? "DATE"
                : isPerWeek ? "END (1 WEEK)"
                : isPerMonth ? "END (1 MONTH)"
                : isPerYear ? `END (${yearDuration} YR)`
                : "CHECKOUT"}
              </div>
              <div style={{ fontSize:14,fontWeight:700,marginTop:2 }}>
                {isPerHour||!checkOut ? dayjs(checkIn).format("M/D/YYYY") : dayjs(checkOut).format("M/D/YYYY")}
              </div>
            </div>
          </div>
          
          <div onClick={()=>setShowHoursModal(true)} style={{ borderTop:`1px solid ${T.pearl}`,padding:"10px 12px",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center" }}>
            <div>
              <div style={{ fontSize:10,fontWeight:800,letterSpacing:"0.1em",color:T.smoke }}>GUESTS</div>
              <div style={{ fontSize:14,fontWeight:700,marginTop:2 }}>{totalGuests} guest{totalGuests>1?"s":""}</div>
            </div>
            <span style={{ fontSize:12,fontWeight:700,color:T.gold,textDecoration:"underline" }}>Edit</span>
          </div>
        </div>
      )}

      {checkIn && checkOut && !isPerHour && (
        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:14, padding:"8px 12px", background:T.goldSubtle, borderRadius:8, border:`1px solid ${T.goldBorder}` }}>
          <Clock size={12} color={T.gold}/>
          <span style={{ fontSize:11, color:T.goldDark, fontWeight:600 }}>
            Tip: double-click a date on the calendar to set it as check-in
          </span>
        </div>
      )}

      {/* EXTRAS */}
      {listing?.extras && Object.keys(listing.extras).length > 0 && (
        <div style={{ marginBottom:16,padding:"12px 14px",background:T.offwhite,borderRadius:12 }}>
          <div style={{ fontSize:12,fontWeight:700,letterSpacing:"0.08em",color:T.smoke,marginBottom:10,textTransform:"uppercase" }}>Add-ons</div>
          {Object.entries(listing.extras).map(([name, price]) => {
            const isCleaning = name.toLowerCase().includes("clean");
            const checked    = isCleaning || selectedExtras.includes(name);
            return (
              <div key={name} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",opacity:isCleaning?.6:1 }}>
                <label style={{ display:"flex",gap:8,alignItems:"center",cursor:isCleaning?"default":"pointer",fontSize:13,fontWeight:600 }}>
                  <input type="checkbox" checked={checked} disabled={isCleaning} onChange={()=>toggleExtra(name)} style={{ accentColor:T.gold }}/>
                  {name}
                </label>
<span style={{ fontSize:13,fontWeight:700,color:T.black }}>{symbol}{Number(convertRaw(Number(price), "EUR")).toLocaleString()}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* CANCELLATION */}
      {formattedPolicy && (
        <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:14,fontSize:12,color:T.smoke }}>
          <Check size={13} color={T.green}/>
          <span style={{ fontWeight:600 }}>{formattedPolicy} cancellation</span>
        </div>
      )}

      {/* RESERVE BUTTON */}
<button
  ref={reserveBtnRef}
  onClick={
    alreadyRequested || requestDeclined
      ? undefined
      : handleContinue          // works for both approved and normal flow
  }
  disabled={alreadyRequested || requestDeclined}
  style={{
    ...S.reserveBtn,
    background: requestDeclined
      ? "#888780"                                                  // gray
      : alreadyRequested
      ? "#B4B2A9"                                                  // light gray
      : requestApproved || !isApproveMode
      ? T.red                                                      // normal red
      : "linear-gradient(135deg,#BA7517,#9A5F10)",                 // amber (pending request)
    boxShadow: requestDeclined || alreadyRequested
      ? "none"
      : requestApproved || !isApproveMode
      ? "0 6px 20px rgba(204,0,0,.4)"
      : "0 6px 20px rgba(186,117,23,.4)",
    cursor: alreadyRequested || requestDeclined ? "not-allowed" : "pointer",
    opacity: alreadyRequested || requestDeclined ? 0.7 : 1,
  }}
>
  {reserveBtnLabel}
</button>


<div style={{ textAlign:"center",fontSize:12,color:T.smoke,marginTop:10 }}>
        {requestApproved
          ? "Complete your booking below"
          : isApproveMode
          ? "You won't be charged until host confirms"
          : "You won't be charged yet"}
      </div>

      {requestDeclined ? (
        <div style={{
          marginTop:10, padding:"10px 12px", borderRadius:10,
          background:"#FCEBEB", border:"1px solid #F09595",
          fontSize:12, color:"#791F1F", lineHeight:1.5,
          display:"flex", gap:8, alignItems:"flex-start",
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#A32D2D"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ flexShrink:0, marginTop:1 }}>
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          <span>Your request was declined by the host. You may contact them for details.</span>
        </div>
      ) : requestApproved ? (
        <div style={{
          marginTop:10, padding:"10px 12px", borderRadius:10,
          background:"#EAF3DE", border:"1px solid #C0DD97",
          fontSize:12, color:"#27500A", lineHeight:1.5,
          display:"flex", gap:8, alignItems:"flex-start",
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3B6D11"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ flexShrink:0, marginTop:1 }}>
            <circle cx="12" cy="12" r="10"/>
            <polyline points="9 12 11 14 15 10"/>
          </svg>
          <span>Your request was approved! Tap Reserve to complete your booking.</span>
        </div>
      ) : isApproveMode ? (
        <div style={{
          marginTop:10, padding:"10px 12px", borderRadius:10,
          background:"#FAEEDA", border:"1px solid #FAC775",
          fontSize:12, color:"#633806", lineHeight:1.5,
          display:"flex", gap:8, alignItems:"flex-start",
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#BA7517"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ flexShrink:0, marginTop:1 }}>
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
          <span>This listing requires host approval. You won't be charged until they confirm.</span>
        </div>
      ) : null}

      <MissingModal desktop/>
    </aside>
  );

  /* ─────────────────────────────────────────────────
     MOBILE RETURN
  ───────────────────────────────────────────────── */
  if (!isDesktop) {
    return (
      <div style={{ background:T.white,minHeight:"100vh",fontFamily:T.sans,paddingBottom:100 }}>
    <GalleryLightbox/>
    <GalleryGrid/>
<HoursAndGuestsModal open={showHoursModal} onClose={()=>setShowHoursModal(false)}/>
<MissingModal/>
<DescriptionModal/>
        {/* MOBILE GALLERY — full-width horizontal scroll */}
        
        
<div style={{ width:"100%", position:"relative" }}>
          <div style={{ overflowX:"auto",overflowY:"hidden",WebkitOverflowScrolling:"touch" }}>
            <div style={{ display:"flex" }}>
              {photos.map((src,i) => (
                <div key={i} style={{ flexShrink:0,position:"relative" }}>
                  <img src={src} alt="" onClick={()=>{setGalleryIdx(i);setGalleryOpen(true);}} style={{ width:"100vw",height:"70vw",maxHeight:420,objectFit:"cover",display:"block",cursor:"pointer" }}/>
                  <div style={{ position:"absolute",bottom:12,right:12,background:"rgba(0,0,0,.7)",color:T.white,fontSize:12,fontWeight:700,borderRadius:999,padding:"4px 10px" }}>{i+1}/{photos.length}</div>
                </div>
              ))}
            </div>
          </div>
          {/* animated gradient bar at bottom */}
          <div style={{ position:"absolute", bottom:0, left:0, right:0, height:4, background:"linear-gradient(90deg,#0a0a0a,#bf9b30,#d46b10,#e84020,#bf9b30,#0a0a0a)", backgroundSize:"300% 100%", animation:"aeSpin 3s linear infinite" }}/>
        </div>

        {/* GUEST FAVOURITE MOBILE BADGE */}
    {(isGuestFavourite||isHighlyRated) && (
  <>
    <style>{`
      @keyframes spin-border {
        from { transform: rotate(0deg); }
        to   { transform: rotate(360deg); }
      }
    `}</style>
    <div style={{ margin:"12px 16px 0", borderRadius:18, padding:3, position:"relative", overflow:"hidden" }}>
      <div style={{
        position:"absolute", inset:-40,
        background:"conic-gradient(#C8860A, #F5DF8E, #8250FF, #00C8A0, #C8860A)",
        animation:"spin-border 2.8s linear infinite",
        borderRadius:"50%",
      }}/>
      <div style={{ position:"relative", background:"#FFFDF7", borderRadius:15, padding:"14px 18px", display:"flex", alignItems:"center", gap:14 }}>
        <div style={{ width:50, height:50, borderRadius:"50%", background:"#FDF6E3", border:"1.5px solid #EAD08A", display:"grid", placeItems:"center", flexShrink:0 }}>
          <span style={{ fontSize:24 }}>🏆</span>
        </div>
        <div>
          <div style={{ fontWeight:800, fontSize:15, color:"#1A1A1A" }}>Guest Favourite</div>
          <div style={{ fontSize:12, color:"#8A8070", marginTop:2 }}>One of the most loved homes on ARELUXE</div>
        </div>
      </div>
    </div>
  </>
)}

        {/* MOBILE HERO INFO */}
        <div style={{ padding:"16px 16px 0" }}>
          {/* Policy + room type pills */}
          <div style={{ display:"flex",flexWrap:"wrap",gap:8,marginBottom:14 }}>
            {rateLabel && <div style={S.pill("green")}>{rateLabel}</div>}
            {roomTypeLabel && <div style={S.pill("gold")}>{roomTypeLabel}</div>}
            {formattedPolicy && (
              <div onClick={()=>window.open("https://areluxe.com/pages/terms","_blank")} style={S.pill("red")}>{formattedPolicy} Cancellation</div>
            )}
          </div>

          <h1 style={{ fontSize:24,fontWeight:900,fontFamily:T.serif,color:T.black,marginBottom:8 }}>{facts.title}</h1>

          {/* FACTS SCROLL */}
          <div style={{ display:"flex",gap:16,overflowX:"auto",paddingBottom:8,marginBottom:12,scrollbarWidth:"none" }}>
            {facts.squareMeters>0 && <FactChip icon={<Ruler size={18}/>} value={`${facts.squareMeters} m²`} image={squarefeetIcon}/>}
            {facts.guests>0 && <FactChip icon={<Users size={18}/>} value={`${facts.guests} guests`} image={guestsIcon}/>}
            {facts.bedrooms>0 && <FactChip icon={<Bed size={18}/>} value={`${facts.bedrooms} bed${facts.bedrooms>1?"s":""}`} image={bedroomIcon}/>}
            {facts.beds>0 && <FactChip icon={<BedDouble size={18}/>} value={`${facts.beds} beds`} image={bedIcon}/>}
            {facts.baths>0 && <FactChip icon={<Bath size={18}/>} value={`${facts.baths} bath${facts.baths>1?"s":""}`} image={bathtubIcon}/>}
{facts.parkingLots>0 && <FactChip icon={<CarFront size={18}/>} value={`${facts.parkingLots} parking`} image={parkingIcon}/>}
{(listing?.toilets ?? 0)>0 && <FactChip icon={<Toilet size={18}/>} value={`${listing?.toilets} toilet${listing?.toilets>1?"s":""}`} image={toiletIcon}/>}          </div>

          {/* RATING */}
          <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:16 }}>
            <Star size={15} fill={T.gold} color={T.gold}/>
            {avgRating ? (
              <>
                <strong style={{ fontSize:15 }}>{avgRating}</strong>
                <span style={{ fontSize:14,color:T.smoke }}>· {reviewCount} review{reviewCount!==1?"s":""}</span>
              </>
            ) : <span style={{ fontSize:14,color:T.smoke }}>New listing</span>}
          </div>

          {hostUser && !isSystemListing && (
  <div style={{ display:"flex",alignItems:"center",gap:12,padding:"14px 0",borderTop:`1px solid ${T.pearl}`,borderBottom:`1px solid ${T.pearl}`,marginBottom:16 }}>
    <div style={{ width:44,height:44,borderRadius:"50%",background:`url(${hostUser.profilePicture|| hostIcon }) center/cover`,flexShrink:0,boxShadow:`0 0 0 2px ${T.gold}` }}/>
    <div>
      <div style={{ fontWeight:700,fontSize:15 }}>Hosted by {hostUser.firstName}</div>
      <div style={{ fontSize:12,color:T.smoke,display:"flex",alignItems:"center",gap:4 }}>
        {hostUser.isVerified ? <><ShieldCheck size={12} color={T.green}/> Identity verified</> : <><ShieldOff size={12} color={T.red}/> Not verified</>}
      </div>
    </div>
  </div>
)}
        
        </div>

        {/* MOBILE BOOKING CARD */}
        <div style={{ padding:"0 16px" }}><BookingCard/></div>

        {!authUser && <div style={{ padding:"0 16px 24px" }}><GateOverlay navigate={navigate} /></div>}

        {authUser && <>

        {/* MOBILE CALENDAR */}
        <div style={{ padding:"24px 16px" }}>
          <h2 style={{ fontFamily:T.serif,fontSize:22,fontWeight:700,marginBottom:16,color:T.black }}>Select Dates</h2>
          <CalendarTabs/>
          {tab==="dates" && (() => {
            // how many months to show, loaded 3 at a time up to 12
            const monthsVisible = monthsToShow; // already in state, capped at 12
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
                {Array.from({ length: monthsVisible }).map((_, i) => {
                  const m = dayjs().startOf("month").add(i, "month");
                  return (
                    <div
                      key={m.toString()}
                      style={{
                        border: `1px solid ${T.pearl}`,
                        borderRadius: 20,
                        padding: "20px 16px",
                        background: T.white,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.06), 0 1px 0 rgba(255,255,255,0.9) inset",
                        transform: "perspective(600px) rotateX(0deg)",
                        transition: "box-shadow 0.2s ease",
                      }}
                    >
                      {renderCalendarMonth(m, true)}
                    </div>
                  );
                })}

                {/* 3D scroll sentinel + loader */}
                <div ref={loaderRef} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "16px 0 8px" }}>
                  {monthsToShow < 12 ? (
                    <>
                      {/* 3D spinning coin loader */}
                      <style>{`
                        @keyframes coin3d {
                          0%   { transform: perspective(200px) rotateY(0deg);   }
                          100% { transform: perspective(200px) rotateY(360deg);  }
                        }
                        @keyframes fadeUp {
                          from { opacity:0; transform: translateY(8px); }
                          to   { opacity:1; transform: translateY(0);   }
                        }
                      `}</style>
                      <div style={{
                        width: 36, height: 36, borderRadius: "50%",
                        background: GOLD_GRAD,
                        boxShadow: "0 4px 14px rgba(201,168,76,0.5), 0 1px 0 rgba(255,255,255,0.6) inset",
                        animation: "coin3d 1.2s linear infinite",
                      }}/>
                      <span style={{
                        fontSize: 12, fontWeight: 700, color: T.smoke,
                        letterSpacing: "0.06em", textTransform: "uppercase",
                        animation: "fadeUp 0.4s ease",
                      }}>
                        Loading more months…
                      </span>
                    </>
                  ) : (
                    <span style={{ fontSize: 12, color: T.smoke, fontWeight: 600 }}>
                      Showing 1 year of availability
                    </span>
                  )}
                </div>
              </div>
            );
          })()}
          {tab==="months" && renderMonthsTab()}
          {tab==="flex" && renderFlexTab()}
          {tab==="yearly" && !isPerHour && renderYearlyTab()}
        </div>

        <div style={S.divider}/>

        {/* DESCRIPTION */}
       {/* DESCRIPTION */}
{listing && (
  <div style={{ padding:"24px 16px" }}>
    <h2 style={{ fontFamily:T.serif,fontSize:22,fontWeight:700,marginBottom:14,color:T.black }}>About this place</h2>
    <div style={{ fontSize:15,lineHeight:1.8,color:T.black }}>{renderDescription(listing.description)}</div>
  </div>
)}

        <div style={S.divider}/>

        {/* AMENITIES */}
        {listing && (
          <div style={{ padding:"0 16px" }}>
            {renderAllAmenitiesSection()}
          </div>
        )}

        <div style={S.divider}/>

        {/* MAP */}
        {listing?.address?.latitude && (
          <div style={{ padding:"24px 16px" }}>
            <h2 style={{ fontFamily:T.serif,fontSize:22,fontWeight:700,marginBottom:6,color:T.black }}>Where you'll be</h2>
            <p style={{ fontSize:14,color:T.smoke,marginBottom:14 }}>{listing.address?.street}, {listing.address?.city||listing.address?.county}, {listing.address?.country}</p>
            <div style={{ borderRadius:16,overflow:"hidden",height:280 }}>
              <GoogleMap mapContainerStyle={{width:"100%",height:"100%"}} zoom={15} center={{lat:Number(listing.address.latitude),lng:Number(listing.address.longitude)}} options={{disableDefaultUI:true,zoomControl:true,gestureHandling:"cooperative"}}>
                <Marker position={{lat:Number(listing.address.latitude),lng:Number(listing.address.longitude)}}/>
              </GoogleMap>
            </div>
          </div>
        )}

        <div style={S.divider}/>

        {/* REVIEWS */}
        <div style={{ padding:"24px 16px" }}>
          <h2 style={{ fontFamily:T.serif,fontSize:22,fontWeight:700,marginBottom:16,color:T.black }}>
            <Star size={18} style={{verticalAlign:"middle",marginRight:6}} fill={T.gold} color={T.gold}/>
            {avgRating||"New"} · {shortRatings.length} reviews
          </h2>
          <div style={{ display:"flex",gap:16,overflowX:"auto",paddingBottom:8,scrollbarWidth:"none" }}>
            {top5Ratings.map((rating: any,idx: number) => {
              const guest = allUsers.find((u: any)=>u.userId===rating.guestId);
              const name = guest?.firstName ? guest.firstName.slice(0,3)+"***" : "Guest";
              return (
                <div key={idx} style={{ minWidth:"78vw",background:T.offwhite,borderRadius:16,padding:"16px 18px",flexShrink:0 }}>
                  <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:8 }}>
                    <div style={{ width:36,height:36,borderRadius:"50%",background:GOLD_GRAD,color:T.black,display:"grid",placeItems:"center",fontWeight:800,fontSize:14,overflow:"hidden" }}>
                      {guest?.profilePicture ? <img src={guest.profilePicture} style={{width:"100%",height:"100%",objectFit:"cover"}}/> : name.charAt(0)}
                    </div>
                    <div>
                      <strong style={{fontSize:14}}>{name}</strong>
                      {renderStars(rating.listingRating)}
                    </div>
                  </div>
                  <p style={{ fontSize:14,lineHeight:1.6,color:T.black,margin:0 }}>
                    {rating.guestReview.length>120?`${rating.guestReview.slice(0,110)}…`:rating.guestReview}
                  </p>
                  <div style={{ fontSize:12,color:T.smoke,marginTop:8 }}>{formatRelativeDate(rating.dateCreated)}</div>
                </div>
              );
            })}
          </div>
          <button onClick={()=>navigate(`/reviews/${listingId}`)} style={S.outlineBtn}>Show all {shortRatings.length} reviews</button>
        </div>

        <div style={S.divider}/>

        {/* HOST LISTINGS */}
        {listing?.hostId && !isSystemListing && (
          <div style={{ padding:"24px 16px" }}>
            <h2 style={{ fontFamily:T.serif,fontSize:20,fontWeight:700,marginBottom:14,color:T.black }}>More from {host.firstName}</h2>
            <HostShortHomes hostId={listing.hostId}/>
          </div>
        )}

      <div style={S.divider}/>

        </> }

        {/* HOST SECTION
        
        
             {hostUser && (
          <div style={{ padding:"24px 16px" }}>
            <h2 style={{ fontFamily:T.serif,fontSize:22,fontWeight:700,marginBottom:20,color:T.black }}>Meet Your Host</h2>
            <div style={{ background:T.white,borderRadius:20,padding:20,boxShadow:"0 8px 32px rgba(0,0,0,.1)",marginBottom:20 }}>
              <div style={{ display:"flex",alignItems:"center",gap:16,marginBottom:16 }}>
                <div onClick={()=>navigate(`/aboutme/${hostUser.username}`)} style={{ width:72,height:72,borderRadius:"50%",background:hostUser.profilePicture?`url(${hostUser.profilePicture}) center/cover`:GOLD_GRAD,color:T.white,display:"grid",placeItems:"center",fontWeight:900,fontSize:28,cursor:"pointer",boxShadow:`0 4px 16px ${T.gold}40`,flexShrink:0 }}>
                  {!hostUser.profilePicture&&(hostUser.firstName?.charAt(0)||"H")}
                </div>
                <div>
                  <div style={{ fontSize:20,fontWeight:800,fontFamily:T.serif }}>{hostUser.firstName}</div>
                  <div style={{ display:"flex",alignItems:"center",gap:4,fontSize:13,fontWeight:600,color:hostUser.isVerified?T.green:T.red,marginTop:4 }}>
                    {hostUser.isVerified?<><ShieldCheck size={14}/> Identity verified</>:<><ShieldOff size={14}/> Not verified</>}
                  </div>
                </div>
              </div>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:16 }}>
                {[{v:hostReviewsCount,l:"Reviews"},{v:hostAvgRating.toFixed(1),l:"Rating"},{v:formatHostingDuration(hostUser.joinDate),l:"Hosting"}].map(item=>(
                  <div key={item.l} style={{ textAlign:"center",padding:"12px 0",background:T.offwhite,borderRadius:12 }}>
                    <div style={{ fontWeight:800,fontSize:20,fontFamily:T.serif,color:T.black }}>{item.v}</div>
                    <div style={{ fontSize:11,color:T.smoke,marginTop:2 }}>{item.l}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize:14,lineHeight:1.75,color:T.black,marginBottom:16 }}>{renderAboutMe(hostUser.aboutMe)}</div>
   <button onClick={()=>authUser?navigate(`/imessages/${makeThreadId(authUser.userId, hostUser.userId)}`):navigate("/AuthSwitch")} style={{ width:"100%",padding:"12px 0",borderRadius:999,border:`1px solid ${T.black}`,background:T.white,fontWeight:700,fontSize:14,cursor:"pointer" }}>
                <MessageCircle size={15} style={{verticalAlign:"middle",marginRight:6}}/>Message {hostUser.firstName}
              </button>
            </div>
          </div>
        )}
        
        */}
   

        {/* WARNING TOAST */}
        {warningMsg && (
          <div style={{ position:"fixed",bottom:120,left:"50%",transform:"translateX(-50%)",background:T.red,color:T.white,padding:"12px 20px",borderRadius:12,zIndex:2000,fontWeight:700,fontSize:14,boxShadow:"0 8px 24px rgba(204,0,0,.4)",whiteSpace:"nowrap" }}>
            {warningMsg}
          </div>
        )}

        {/* MOBILE STICKY FOOTER */}
        <div style={{ position:"fixed",bottom:0,left:0,right:0,background:T.white,borderTop:`1px solid ${T.pearl}`,padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",zIndex:1200,boxShadow:"0 -4px 20px rgba(0,0,0,.1)" }}>
          <div>
            <div style={{ fontSize:17,fontWeight:900,color:T.black,fontFamily:T.serif }}>{rateLabel||"Contact host"}</div>
            {formattedPolicy && <div style={{ fontSize:12,color:T.green,fontWeight:600,marginTop:2 }}>{formattedPolicy} cancellation</div>}
          </div>
<button
  ref={reserveBtnRef}
  onClick={
    alreadyRequested || requestDeclined
      ? undefined
      : handleContinue          // works for both approved and normal flow
  }
  disabled={alreadyRequested || requestDeclined}
  style={{
    ...S.reserveBtn,
    background: requestDeclined
      ? "#888780"                                                  // gray
      : alreadyRequested
      ? "#B4B2A9"                                                  // light gray
      : requestApproved || !isApproveMode
      ? T.red                                                      // normal red
      : "linear-gradient(135deg,#BA7517,#9A5F10)",                 // amber (pending request)
    boxShadow: requestDeclined || alreadyRequested
      ? "none"
      : requestApproved || !isApproveMode
      ? "0 6px 20px rgba(204,0,0,.4)"
      : "0 6px 20px rgba(186,117,23,.4)",
    cursor: alreadyRequested || requestDeclined ? "not-allowed" : "pointer",
    opacity: alreadyRequested || requestDeclined ? 0.7 : 1,
  }}
>
  {reserveBtnLabel}
</button>
        </div>

        {/* REVIEW MODAL */}
        {selectedReview && (
          <div onClick={()=>setSelectedReview(null)} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:8000,padding:20 }}>
            <div onClick={e=>e.stopPropagation()} style={{ background:T.white,borderRadius:16,padding:24,maxWidth:480,width:"100%" }}>
              <p style={{ fontSize:15,lineHeight:1.8,color:T.black,marginBottom:16 }}>{selectedReview}</p>
              <button onClick={()=>setSelectedReview(null)} style={{ background:T.pearl,border:"none",borderRadius:8,padding:"8px 16px",cursor:"pointer",fontWeight:600 }}>Close</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ─────────────────────────────────────────────────
     DESKTOP RETURN
  ───────────────────────────────────────────────── */
  return (
    <div style={{ background:T.white,minHeight:"100vh",fontFamily:T.sans }}>
      <GalleryLightbox/>
      <GalleryGrid/>
      <HoursAndGuestsModal open={showHoursModal} onClose={()=>setShowHoursModal(false)}/>
      <MissingModal desktop/>
      <DescriptionModal/>

      {/* ── STICKY HEADER ── */}
      <header style={{ position:"sticky",top:0,zIndex:50,background:T.white,borderBottom:`1px solid ${scrolled?T.pearl:"transparent"}`,padding:"14px 24px",display:"flex",justifyContent:"space-between",alignItems:"center",transition:"border-color .3s",boxShadow:scrolled?"0 2px 12px rgba(0,0,0,.06)":"none" }}>
        <button onClick={()=>navigate(-1)} style={{ display:"flex",alignItems:"center",gap:6,background:"transparent",border:`1px solid ${T.pearl}`,borderRadius:999,padding:"8px 14px",fontSize:13,fontWeight:600,cursor:"pointer",color:T.black }}>
          <ArrowLeft size={14}/> Back
        </button>
        <div style={{ fontFamily:T.serif,fontWeight:700,fontSize:18,color:T.black,flex:1,textAlign:"center",paddingLeft:32 }}>
          {listing?.title || "Short Stay"}
        </div>
        <div style={{ display:"flex",gap:10 }}>
          <button onClick={()=>{navigator.clipboard?.writeText(window.location.href);setShareToast(true);}} style={S.headerIconBtn}>
            <Share2 size={16}/>
            <span style={{ fontSize:12,fontWeight:600 }}>Share</span>
          </button>
          <button onClick={()=>setLikedListing(v=>!v)} style={{ ...S.headerIconBtn,color:likedListing?T.red:T.black }}>
            <Heart size={16} fill={likedListing?T.red:"none"} color={likedListing?T.red:T.black}/>
            <span style={{ fontSize:12,fontWeight:600 }}>Save</span>
          </button>
        </div>
      </header>

      {shareToast && (
        <div style={{ position:"fixed",top:70,right:24,background:T.black,color:T.white,padding:"10px 18px",borderRadius:12,zIndex:1000,fontSize:13,fontWeight:600 }}>Link copied!</div>
      )}

      {/* ── GALLERY ── */}
      <div style={{ maxWidth:MAX_WIDTH,margin:"0 auto",padding:"20px 24px 0" }}>
        <style>{`@keyframes aeSpin{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}`}</style>
        <div style={{ position:"relative", borderRadius:22, padding:3, overflow:"hidden" }}>
          <div style={{ position:"absolute", inset:-2, borderRadius:24, background:"linear-gradient(90deg,#0a0a0a,#bf9b30,#d46b10,#e84020,#bf9b30,#0a0a0a)", backgroundSize:"300% 100%", animation:"aeSpin 3s linear infinite", zIndex:0 }}/>
          <div style={{ position:"relative", zIndex:1, display:"grid", gridTemplateColumns:"2fr 1fr 1fr", gridTemplateRows:"repeat(2,240px)", gap:8, borderRadius:20, overflow:"hidden" }}>
            {photos.slice(0,5).map((src,i) => {
              const pos = i===0?{gridColumn:"1",gridRow:"1 / span 2"}:i===1?{gridColumn:"2",gridRow:"1"}:i===2?{gridColumn:"3",gridRow:"1"}:i===3?{gridColumn:"2",gridRow:"2"}:{gridColumn:"3",gridRow:"2"};
              return (
                <div key={i} style={{ ...pos,overflow:"hidden",cursor:"pointer" }} onClick={()=>{setGalleryIdx(i);setGalleryOpen(true);}}>
                  <img src={src} alt="" style={{ width:"100%",height:"100%",objectFit:"cover",display:"block",transition:"transform .35s" }} onMouseEnter={e=>(e.currentTarget.style.transform="scale(1.03)")} onMouseLeave={e=>(e.currentTarget.style.transform="scale(1)")}/>
                </div>
              );
            })}
        <button onClick={()=>setGalleryGridOpen(true)} style={{ position:"absolute",right:16,bottom:16,display:"flex",alignItems:"center",gap:8,padding:"9px 16px",borderRadius:12,background:T.white,border:`1px solid ${T.pearl}`,fontWeight:700,cursor:"pointer",fontSize:13,boxShadow:"0 2px 8px rgba(0,0,0,.12)" }}>
  <Grid size={15}/> Show all photos
</button>
          </div>
        </div>
      </div>

      {/* ── MAIN LAYOUT ── */}
      
      <div style={{ maxWidth:MAX_WIDTH, margin:"0 auto", padding:"32px 24px", display:"grid", gridTemplateColumns:"1fr 420px", gap:48, alignItems:"start" }}>
        {/* LEFT COLUMN */}
        <div style={{ minWidth:0 }}>

          {/* PILLS */}
          <div style={{ display:"flex",flexWrap:"wrap",gap:8,marginBottom:16 }}>
            {rateLabel && <div style={S.pill("green")}>{rateLabel}</div>}
            {roomTypeLabel && <div style={S.pill("gold")}>{roomTypeLabel}</div>}
            {formattedPolicy && <div onClick={()=>window.open("https://areluxe.com/pages/terms","_blank")} style={S.pill("red")}>{formattedPolicy} Cancellation</div>}
          </div>

          {/* GUEST FAVOURITE */}
     {(isGuestFavourite||isHighlyRated) && (
  <>
    <style>{`
      @keyframes spin-border {
        from { transform: rotate(0deg); }
        to   { transform: rotate(360deg); }
      }
    `}</style>
    <div style={{ borderRadius:18, padding:3, position:"relative", overflow:"hidden", width:"fit-content", marginBottom:18 }}>
      <div style={{
        position:"absolute", inset:-40,
        background:"conic-gradient(#C8860A, #F5DF8E, #8250FF, #00C8A0, #C8860A)",
        animation:"spin-border 2.8s linear infinite",
        borderRadius:"50%",
      }}/>
      <div style={{ position:"relative", background:"#FFFDF7", borderRadius:15, padding:"14px 18px", display:"flex", alignItems:"center", gap:14 }}>
        <div style={{ width:50, height:50, borderRadius:"50%", background:"#FDF6E3", border:"1.5px solid #EAD08A", display:"grid", placeItems:"center", flexShrink:0 }}>
          <span style={{ fontSize:24 }}>🏆</span>
        </div>
        <div>
          <div style={{ fontWeight:800, fontSize:15, color:"#1A1A1A" }}>Guest Favourite</div>
          <div style={{ fontSize:12, color:"#8A8070", marginTop:2 }}>One of the most loved homes on ARELUXE</div>
        </div>
      </div>
    </div>
  </>
)}

          <h1 style={{ fontSize:32,fontWeight:900,fontFamily:T.serif,color:T.black,marginBottom:10,lineHeight:1.2 }}>{facts.title}</h1>

          {/* FACTS GRID */}
          <div style={{ display:"flex",flexWrap:"wrap",gap:20,margin:"16px 0 20px" }}>
            {facts.squareMeters>0 && <FactChip icon={<Ruler size={20}/>} value={`${facts.squareMeters} m²`} large image={squarefeetIcon}/>}
            {facts.guests>0 && <FactChip icon={<Users size={20}/>} value={`${facts.guests} guests`} large image={guestsIcon}/>}
            {facts.bedrooms>0 && <FactChip icon={<Bed size={20}/>} value={`${facts.bedrooms} bedroom${facts.bedrooms>1?"s":""}`} large image={bedroomIcon}/>}
            {facts.beds>0 && <FactChip icon={<BedDouble size={20}/>} value={`${facts.beds} bed${facts.beds>1?"s":""}`} large image={bedIcon}/>}
            {facts.baths>0 && <FactChip icon={<Bath size={20}/>} value={`${facts.baths} bath${facts.baths>1?"s":""}`} large image={bathtubIcon}/>}
{facts.parkingLots>0 && <FactChip icon={<CarFront size={20}/>} value={`${facts.parkingLots} parking`} large image={parkingIcon}/>}
{(listing?.toilets ?? 0)>0 && <FactChip icon={<Toilet size={20}/>} value={`${listing?.toilets} toilet${listing?.toilets>1?"s":""}`} large image={toiletIcon}/>}          </div>


          {/* RATING */}
          <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:20 }}>
            <Star size={17} fill={T.gold} color={T.gold}/>
            {avgRating ? (
              <>
                <strong style={{ fontSize:17,color:T.black }}>{avgRating}</strong>
                <span style={{ color:T.smoke,fontSize:15 }}>· {reviewCount} review{reviewCount!==1?"s":""}</span>
                <span style={{ color:T.silver }}>·</span>
                <MapPin size={14} color={T.smoke}/>
                <span style={{ fontSize:14,color:T.smoke }}>{listing?.address?.city}, {listing?.address?.country}</span>
              </>
            ) : (
              <span style={{ fontSize:15,color:T.smoke }}>New · No reviews yet</span>
            )}
          </div>

          <div style={S.divider}/>

          {hostUser && !isSystemListing && (
  <div onClick={()=>document.getElementById("host-section")?.scrollIntoView({behavior:"smooth"})} style={{ display:"flex",alignItems:"center",gap:14,padding:"20px 0",cursor:"pointer" }}>
    <div style={{ position:"relative" }}>
      <div style={{ width:50,height:50,borderRadius:"50%",background:`url(${hostUser.profilePicture||hostIcon}) center/cover`,boxShadow:`0 0 0 2px ${T.gold}` }}/>
      {hostUser.isVerified && (
        <div style={{ position:"absolute",bottom:-2,right:-2,width:18,height:18,borderRadius:"50%",background:T.white,display:"grid",placeItems:"center",boxShadow:"0 1px 4px rgba(0,0,0,.2)" }}>
          <ShieldCheck size={11} color={T.green} strokeWidth={2.5}/>
        </div>
      )}
    </div>
    <div>
      <div style={{ fontWeight:700,fontSize:16 }}>Hosted by {hostUser.firstName}</div>
      <div style={{ fontSize:13,color:T.smoke }}>{formatHostingDuration(hostUser.joinDate)} hosting · {hostReviewsCount} reviews</div>
    </div>
  </div>
)}
       

          <div style={S.divider}/>

          {/* DESCRIPTION */}
        {/* DESCRIPTION */}
{listing && (
  <section style={S.section}>
    <h2 style={S.sectionTitle}>About this place</h2>
    <div style={{ maxWidth:680 }}>{renderDescription(listing.description)}</div>
  </section>
)}

          <div style={S.divider}/>

       {!authUser && <GateOverlay navigate={navigate} />}

          {authUser && <>

          {/* CALENDAR */}
          <section style={{ ...S.section, overflow: "visible" }}>
            <h2 style={S.sectionTitle}>{isPerHour?"Select Date & Hours":"Select Dates"}</h2>

            {checkIn && checkOut && !isPerHour && (
              <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:16,padding:"10px 16px",background:T.goldSubtle,borderRadius:10,border:`1px solid ${T.goldBorder}`,width:"fit-content" }}>
                <Calendar size={15} color={T.gold}/>
                <span style={{ fontSize:14,fontWeight:700,color:T.goldDark }}>
                  {dayjs(checkIn).format("MMM D")} → {dayjs(checkOut).format("MMM D, YYYY")}
                  <span style={{ fontWeight:400,color:T.smoke,marginLeft:8 }}>({dayjs(checkOut).diff(dayjs(checkIn),"day")} nights)</span>
                </span>
              </div>
            )}

            
            
            <div>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20 }}>
                <CalendarTabs/>
                {tab==="dates" && (
                  <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                    
                    <button onClick={()=>setCursor(c=>c.subtract(1,"month"))} disabled={cursor.isSame(dayjs(),"month")||cursor.isBefore(dayjs(),"month")} style={{...S.navBtn,opacity:cursor.isSame(dayjs(),"month")||cursor.isBefore(dayjs(),"month")?0.3:1,cursor:cursor.isSame(dayjs(),"month")||cursor.isBefore(dayjs(),"month")?"not-allowed":"pointer"}}><ChevronLeft size={16}/></button>
                    
                    <span style={{ fontWeight:700,fontSize:14,color:T.black,whiteSpace:"nowrap" }}>
                      {cursor.format("MMM YYYY")} – {cursor.add(2,"month").format("MMM YYYY")}
                    </span>
                    <button onClick={()=>setCursor(c=>c.add(1,"month"))} style={S.navBtn}><ChevronRight size={16}/></button>
                  </div>
                )}
              </div>

{tab==="dates" && (
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:32, alignItems:"start" }}>
                  {renderCalendarMonth(cursor)}
                  {renderCalendarMonth(cursor.add(1,"month"))}
                  {renderCalendarMonth(cursor.add(2,"month"))}
                </div>
              )}
              {tab==="months" && renderMonthsTab()}
              {tab==="flex"   && renderFlexTab()}
              {tab==="yearly" && !isPerHour && renderYearlyTab()}
            </div>

            {/* HOURS PANEL (per-hour listings) */}
            {isPerHour && checkIn && (
              <div style={{ marginTop:20,background:T.white,border:`1px solid ${T.pearl}`,borderRadius:20,padding:24,boxShadow:"0 8px 32px rgba(0,0,0,.06)" }}>
                <h3 style={{ fontFamily:T.serif,fontSize:20,fontWeight:700,marginBottom:4 }}>Available Hours</h3>
                <p style={{ fontSize:13,color:T.smoke,marginBottom:18 }}>Select consecutive time slots</p>
                <div style={{ display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:10 }}>
                  {HOURS_24.map((time,index) => {
                    const isToday  = dayjs(checkIn).isSame(dayjs(),"day");
                    const hourNum  = Number(time.split(":")[0]);
                    const isPastHr = isToday && hourNum < dayjs().hour();
                    const active   = selectedTimes.includes(time);
                    const disabled = isPastHr || !!hourAvailability?.disabledHours?.includes(hourNum);
                    return (
                      <button key={time} disabled={disabled} onClick={()=>!disabled&&handleHourClick(time,index,disabled)} style={{ padding:"10px 0",borderRadius:12,border:active?`2px solid ${T.gold}`:`1px solid ${T.pearl}`,background:disabled?T.pearl:active?GOLD_GRAD:T.white,color:disabled?T.silver:T.black,fontWeight:700,fontSize:13,cursor:disabled?"not-allowed":"pointer",opacity:disabled?.4:1,transition:"all .15s" }}>
                        {time}
                      </button>
                    );
                  })}
                </div>
                {selectedTimes.length>0 && (
                  <div style={{ marginTop:14,padding:"10px 16px",background:T.goldSubtle,borderRadius:10,border:`1px solid ${T.goldBorder}`,fontSize:13,fontWeight:700,color:T.goldDark }}>
                    <Clock size={13} style={{verticalAlign:"middle",marginRight:6}}/>{selectedTimes.length}h: {selectedTimes[0]} → {selectedTimes[selectedTimes.length-1]}
                  </div>
                )}
              </div>
            )}
          </section>

          <div style={S.divider}/>

          {/* AMENITIES */}
          {listing && (
            <>
              {renderAllAmenitiesSection()}
              <div style={S.divider}/>
            </>
          )}

          {/* MAP */}
          {listing?.address?.latitude && (
            <section style={S.section}>
              <h2 style={S.sectionTitle}>Where you'll be</h2>
              <p style={{ fontSize:14,color:T.smoke,marginBottom:16,display:"flex",alignItems:"center",gap:6 }}>
                <MapPin size={14} color={T.smoke}/> {listing.address?.street}, {listing.address?.city||listing.address?.county}, {listing.address?.country}
              </p>
              <div style={{ borderRadius:20,overflow:"hidden",height:380,border:`1px solid ${T.pearl}` }}>
                <GoogleMap mapContainerStyle={{width:"100%",height:"100%"}} zoom={15} center={{lat:Number(listing.address.latitude),lng:Number(listing.address.longitude)}} options={{disableDefaultUI:true,zoomControl:true,gestureHandling:"cooperative"}}>
                  <Marker position={{lat:Number(listing.address.latitude),lng:Number(listing.address.longitude)}}/>
                </GoogleMap>
              </div>
            </section>
          )}

          <div style={S.divider}/>

          {/* REVIEWS */}
          <section style={S.section}>
            <h2 style={{ ...S.sectionTitle,display:"flex",alignItems:"center",gap:8 }}>
              <Star size={20} fill={T.gold} color={T.gold}/>
              {avgRating||"New"} · {shortRatings.length} reviews
            </h2>

            {top5Ratings.length === 0 ? (
              <p style={{ color:T.smoke,fontStyle:"italic",fontSize:15 }}>No reviews yet. Be the first to stay here!</p>
            ) : (
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:"32px 64px",marginBottom:32 }}>
                {top5Ratings.map((rating: any,idx: number) => {
                  const guest = allUsers.find((u: any)=>u.userId===rating.guestId);
                  const name  = guest?.firstName ? guest.firstName.slice(0,3)+"***" : "Guest";
                  return (
                    <div key={idx}>
                      <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:8 }}>
                        <div style={{ width:40,height:40,borderRadius:"50%",background:GOLD_GRAD,color:T.black,display:"grid",placeItems:"center",fontWeight:800,fontSize:14,overflow:"hidden",flexShrink:0 }}>
                          {guest?.profilePicture?<img src={guest.profilePicture} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:name.charAt(0)}
                        </div>
                        <div>
                          <strong style={{fontSize:15}}>{name}</strong>
                          {renderStars(rating.listingRating)}
                        </div>
                      </div>
                      <p style={{ fontSize:15,lineHeight:1.7,color:T.black,margin:"0 0 6px" }}>
                        {rating.guestReview.length>160?`${rating.guestReview.slice(0,150)}…`:rating.guestReview}
                      </p>
                      {rating.guestReview.length>160 && (
                        <span onClick={()=>setSelectedReview(rating.guestReview)} style={{ fontSize:14,fontWeight:700,color:T.black,textDecoration:"underline",cursor:"pointer" }}>See more</span>
                      )}
                      <div style={{ fontSize:13,color:T.smoke,marginTop:6 }}>{formatRelativeDate(rating.dateCreated)}</div>
                    </div>
                  );
                })}
              </div>
            )}
            <button onClick={()=>navigate(`/reviews/${listingId}`)} style={S.outlineBtn}>Show all {shortRatings.length} reviews</button>
          </section>

          <div style={S.divider}/>

          {/* HOST LISTINGS */}
          {listing?.hostId && !isSystemListing && (
            <section style={S.section}>
              <h2 style={S.sectionTitle}>More from {host.firstName}</h2>
              <HostShortHomes hostId={listing.hostId}/>
            </section>
          )}

          <div style={S.divider}/>
</> }

          {hostUser && !isSystemListing && (
  <section id="host-section" style={S.section}>
    <h2 style={S.sectionTitle}>Meet Your Host</h2>
    <div style={{ display:"grid",gridTemplateColumns:"480px 1fr",gap:40,alignItems:"start" }}>
      <div style={{ background:T.white,borderRadius:20,padding:28,boxShadow:"0 8px 32px rgba(0,0,0,.1)",border:`1px solid ${T.pearl}` }}>
        <div style={{ display:"grid",gridTemplateColumns:"140px 1fr",gap:20 }}>
          <div style={{ display:"flex",flexDirection:"column",alignItems:"center",textAlign:"center" }}>
            <div onClick={()=>navigate(`/u/${hostUser.username}`)} style={{ width:88,height:88,borderRadius:"50%",background:`url(${hostUser.profilePicture||hostIcon}) center/cover`,cursor:"pointer",boxShadow:`0 6px 20px ${T.gold}40`,marginBottom:10 }}/>
            <div style={{ fontSize:18,fontWeight:800,fontFamily:T.serif }}>{hostUser.firstName}</div>
            <div style={{ display:"flex",alignItems:"center",gap:4,fontSize:12,fontWeight:600,color:hostUser.isVerified?T.green:T.red,marginTop:6 }}>
              {hostUser.isVerified?<><ShieldCheck size={12}/> Verified</>:<><ShieldOff size={12}/> Not verified</>}
            </div>
          </div>
          <div>
            {[{v:hostReviewsCount,l:"Reviews"},{v:hostAvgRating.toFixed(1),l:"Rating"},{v:formatHostingDuration(hostUser.joinDate),l:"hosting"}].map((item,i,arr)=>(
              <div key={item.l}>
                <div style={{ textAlign:"center",padding:"12px 0" }}>
                  <div style={{ fontSize:26,fontWeight:800,fontFamily:T.serif,color:T.black }}>{item.v}</div>
                  <div style={{ fontSize:11,color:T.smoke,letterSpacing:"0.06em",textTransform:"uppercase" }}>{item.l}</div>
                </div>
                {i<arr.length-1 && <div style={{ height:1,background:T.pearl }}/>}
              </div>
            ))}
          </div>
        </div>
        <button onClick={()=>authUser?navigate(`/imessages/${makeThreadId(authUser.userId, hostUser.userId)}`):navigate("/AuthSwitch")} style={{ marginTop:20,width:"100%",padding:"13px 0",borderRadius:999,border:`1px solid ${T.black}`,background:T.white,fontWeight:700,fontSize:14,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8 }}>
          <MessageCircle size={16}/> Message {hostUser.firstName}
        </button>
      </div>
      <div>
        <h3 style={{ fontSize:18,fontWeight:700,marginBottom:14,fontFamily:T.serif }}>About {hostUser.firstName}</h3>
        <div style={{ fontSize:15,lineHeight:1.8,color:T.black }}>{renderAboutMe(hostUser.aboutMe)}</div>
        <div onClick={()=>navigate(`/aboutme/${hostUser.username}`)} style={{ marginTop:12,fontSize:14,fontWeight:600,color:T.black,textDecoration:"underline",cursor:"pointer" }}>View full profile →</div>
      </div>
    </div>
  </section>
)}
          
          
        </div>
 
          



        {/* RIGHT — BOOKING CARD */}
       <div style={{ position:"sticky", top:96, alignSelf:"start" }}>
          <BookingCard/>
        </div>
      </div>

      {/* REVIEW FULL MODAL */}
      {selectedReview && (
        <div onClick={()=>setSelectedReview(null)} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:8000,padding:20 }}>
          <div onClick={e=>e.stopPropagation()} style={{ background:T.white,borderRadius:20,padding:28,maxWidth:560,width:"100%",boxShadow:"0 20px 60px rgba(0,0,0,.3)" }}>
            <h3 style={{ fontFamily:T.serif,fontSize:20,fontWeight:700,marginBottom:14 }}>Full Review</h3>
            <p style={{ fontSize:16,lineHeight:1.8,color:T.black,marginBottom:20 }}>{selectedReview}</p>
            <button onClick={()=>setSelectedReview(null)} style={{ background:T.pearl,border:"none",borderRadius:10,padding:"10px 20px",cursor:"pointer",fontWeight:600 }}>Close</button>
          </div>
        </div>
      )}

      {/* WARNING TOAST */}
      {warningMsg && (
        <div style={{ position:"fixed",bottom:30,left:"50%",transform:"translateX(-50%)",background:T.red,color:T.white,padding:"12px 24px",borderRadius:12,zIndex:2000,fontWeight:700,fontSize:14,boxShadow:"0 8px 24px rgba(204,0,0,.4)",whiteSpace:"nowrap" }}>
          {warningMsg}
        </div>
      )}
    </div>
  );
}


const GateOverlay = ({ navigate }: { navigate: (path: string) => void }) => (
  <div
    onClick={() => navigate("/AuthSwitch")}
    style={{
      position: "relative", borderRadius: 20, overflow: "hidden",
      cursor: "pointer", marginTop: 8,
    }}
  >
    <div style={{
      height: 220, background: `linear-gradient(to bottom, ${T.offwhite}, ${T.pearl})`,
      filter: "blur(4px)", pointerEvents: "none",
    }}/>
    <div style={{
      position: "absolute", inset: 0,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      background: "rgba(255,255,255,0.7)", backdropFilter: "blur(2px)",
      gap: 12,
    }}>
      <Crown size={28} color={T.gold}/>
      <div style={{ fontFamily: T.serif, fontSize: 20, fontWeight: 800, color: T.black }}>
        Sign in to see more
      </div>
      <div style={{ fontSize: 13, color: T.smoke, marginBottom: 4 }}>
        View availability, amenities, location , reviews & host details.
      </div>
      <button style={{
        padding: "12px 32px", borderRadius: 999,
        background: T.red, color: T.white,
        fontWeight: 800, fontSize: 15, border: "none", cursor: "pointer",
        boxShadow: `0 4px 16px rgba(204,0,0,.35)`,
      }}>
        View More
      </button>
    </div>
  </div>
);



/* ═══════════════════════════════════════════════════════════
   FACT CHIP
═══════════════════════════════════════════════════════════ */
const FactChip: React.FC<{ icon: React.ReactNode; value: string; large?: boolean; image?: string }> = ({ icon, value, large, image }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = !!image && !imgFailed;
  return (
    <div style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:6,minWidth:large?90:72,flexShrink:0 }}>
      <div style={{ width:70,height:70,borderRadius:12,background:"#F5F5F3",display:"grid",placeItems:"center",color:"#C9A84C" }}>
        {showImage
          ? <img src={image} alt="" style={{ width:70,height:70,objectFit:"contain" }} onError={()=>setImgFailed(true)}/>
          : icon}
      </div>
      <span style={{ fontSize:large?13:11,fontWeight:700,color:"#0A0A0A",textAlign:"center" }}>{value}</span>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════════════ */
const S: Record<string, any> = {
  divider: { height:1, background:T.pearl, margin:"4px 0" },

  section: { padding:"36px 0" },

  sectionTitle: {
    fontSize:22, fontWeight:700, fontFamily:T.serif, color:T.black, marginBottom:20, letterSpacing:"0.01em",
  },

  pill: (variant: "gold"|"green"|"red") => ({
    display:"inline-flex", alignItems:"center",
    padding:"6px 14px", borderRadius:999, fontSize:12, fontWeight:700, letterSpacing:"0.05em",
    cursor: variant==="red" ? "pointer" : "default",
    background: variant==="gold" ? "linear-gradient(135deg,#f5e7b2,#e6cc7a)"
      : variant==="green" ? "#E8F5E9"
      : "linear-gradient(135deg,#ff4d4f,#cc0000)",
    color: variant==="gold" ? "#5c4a12"
      : variant==="green" ? "#1b5e20"
      : T.white,
    boxShadow: variant==="red" ? "0 2px 8px rgba(204,0,0,.3)" : "none",
  }),

  amenGrid: {
    display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))", gap:12,
  },

  amenCard: {
    background:T.offwhite, borderRadius:14, padding:"16px 10px",
    display:"flex", flexDirection:"column", alignItems:"center",
    border:"1px solid",
    transition:"all .2s",
  },

  showMoreBtn: {
    marginTop:18, background:"transparent", border:"none",
    fontSize:14, fontWeight:700, color:T.black, textDecoration:"underline", cursor:"pointer", padding:0,
  },

  outlineBtn: {
    padding:"13px 20px", borderRadius:12, border:`1px solid ${T.pewter}`,
    background:T.white, fontWeight:700, fontSize:14, cursor:"pointer",
    color:T.black, display:"inline-flex", alignItems:"center", gap:8,
    transition:"all .2s",
  },

  navBtn: {
    width:32, height:32, borderRadius:"50%", border:`1px solid ${T.pearl}`,
    background:T.white, display:"grid", placeItems:"center", cursor:"pointer",
    boxShadow:"0 2px 6px rgba(0,0,0,.08)",
  },

  counterBtn: {
    width:38, height:38, borderRadius:10, border:`1px solid ${T.pearl}`,
    background:T.white, fontSize:20, fontWeight:700, cursor:"pointer",
    display:"grid", placeItems:"center", boxShadow:"0 2px 6px rgba(0,0,0,.08)",
  },

 bookingCard: {
    background:T.white, border:`1px solid ${T.pearl}`,
    borderRadius:20, padding:24,
    boxShadow:"0 12px 40px rgba(0,0,0,.10)",
  },

  reserveBtn: {
    width:"100%", padding:"15px 0", borderRadius:12,
    background:T.red, border:"none",
    color:T.white, fontWeight:800, fontSize:16,
    cursor:"pointer", letterSpacing:"0.04em",
    boxShadow:`0 6px 20px rgba(204,0,0,.4)`,
    transition:"transform .15s, box-shadow .15s",
  },

  headerIconBtn: {
    display:"flex", alignItems:"center", gap:6,
    padding:"8px 14px", borderRadius:999,
    border:`1px solid ${T.pearl}`, background:T.white,
    cursor:"pointer", fontSize:13, fontWeight:600, color:T.black,
  },
};